// New York, "Snow over Brooklyn Bridge": a snowy winter night seen from a Brooklyn street.
// Back to front: icy sky, hazy moon, snow clouds -> Manhattan skyline (Midtown behind the bridge: Empire State
// with a slowly colour-cycling crown, Chrysler's stacked arches; the nearer, taller Lower Manhattan cluster on the
// left with One World Trade Center) -> far snow -> Brooklyn Bridge (Gothic towers, cables, stay fan, the raised
// pedestrian promenade with a few walkers, two-way deck traffic) -> East River (reflections, streaks, ice floes, a ferry)
// -> waterfront railing and street (yellow cabs, striped steam stack, coffee cart, subway globes,
// bishop's-crook lamp) -> brick walk-up with zigzag fire escapes and a rooftop water tower
// -> near snow with one loop-safe gust -> vignette. A lamplit billboard on a steel scaffold stands on the
// promenade in front of the river (its face is the exported `billboard`; render(t, { pitch }) swaps its ad).
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, text, reflect, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const SHORE = 184;                  // Manhattan waterline = top of the river
const RAIL = 212;                   // Brooklyn waterfront railing (the river shows down to RAIL + 8)
const DECK = 150;                   // bridge roadway
const TW = [78, 300];               // bridge tower centres (38 px wide)
const T_TOP = 94, T_BASE = 190;     // tower cornice and footing (in the water)
const ESB_X = 189, CHR_X = 252;     // Empire State antenna column, Chrysler needle column
const BX = 374;                     // brick walk-up: left edge of the facade
const ROAD0 = 228, ROAD1 = 252;     // roadway (far lane above, near lane below)
const WALK = 257;                   // near sidewalk top
const LAMP = { x: 34, y: 151 };     // lantern of the bishop's-crook lamp
const MOON = { x: 118, y: 36 };
const STACK = { x: 150, top: 231 }; // orange-and-white steam stack (centre column, top row)
const CART = { x: 50, y: 238 };     // coffee cart (left, top of the awning)
const SUB = { x0: 258, x1: 304 };   // subway entrance posts
const BB = { x: 325, y: 120, w: 44, h: 20 };  // billboard face on the promenade (the pitch is painted exactly here)
// three hidden things (site/js/ui/secrets.js, exported as `secrets`): a snowman someone built on the walk-up's roof, a
// rat hauling a slice of pizza along a window's lintel, and a sleigh that crosses the moon twice a loop
const SNOWMAN = { x: 420, y: 44 };  // sprite origin: it stands in the roof's snow, between the pigeons and the tank's ladder
const RAT = { x: 407, y: 65 };      // sprite origin, on the snowy lintel of a top-floor window
const SLEIGH = [[20, 32], [134, 146]];   // the sleigh is over the moon's disc: 12 s to cross it, while no cloud is in front

/* ================= Pixel helpers ================= */
// ImageData painter for pixel-heavy layers
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const alpha = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  return { c, x, put, alpha, done: () => { x.putImageData(img, 0, 0); return c; } };
}
// integer Bresenham line
function line(c, x0, y0, x1, y1, col) {
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  c.fillStyle = col;
  for (;;) {
    c.fillRect(x0, y0, 1, 1);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
// sprite from rows of characters and a colour key ('.' or unknown = transparent)
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
// dithered soft blob for steam and smoke
function makePuff(r, light, dark) {
  const s = r * 2 + 1, p = painter(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d = Math.hypot(i - r, y - r) / (r + 0.5);
    if (d < 1 && (1 - d) * 1.8 > bayer(i, y) * 0.95) p.put(i, y, i + y < r * 1.7 ? light : dark);
  }
  return p.done();
}
const snowPx = (h1, h2) => h1 < 0.72 ? '#e8f0fc' : h2 < 0.5 ? '#c4d2ea' : '#a6b6d6';

/* ================= Sky ================= */
const SKY = ['#02050d', '#040916', '#070f21', '#0a152c', '#0d1c37', '#112442', '#162c4d', '#1d3557',
  '#263e60', '#324866', '#41526c', '#555b6f', '#6a6470', '#806d6d', '#957668'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 198), 1.5);
    v += Math.max(0, 1 - Math.hypot((i - 200) / 330, (y - 188) / 110)) * 0.28;   // warm city glow
    return v;
  });
}
function renderMoon() {
  const r = 7, s = r * 2 + 1, p = painter(s, s);
  const craters = [[-2, -2, 1.5], [2, 1, 1.3], [-1, 3, 1], [3, -3, 0.8], [-4, 1, 0.7]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.3) continue;
    let col = dx + dy < -5 ? '#f7fbff' : '#e3ebf9';
    if (dd > r - 1.2) col = dx + dy > 0 ? '#b3c2e0' : '#d2dcf1';
    for (const [cx, cy, cr] of craters) if (Math.hypot(dx - cx, dy - cy) < cr) col = '#c5d1ea';
    p.put(i, y, col);
  }
  return p.done();
}
const STARS = [];
{ const r = rng(12); for (let i = 0; i < 24; i++) STARS.push({ x: Math.floor(r() * 372), y: 3 + Math.floor(r() * 78), k: 20 + Math.floor(r() * 60), ph: r(), b: 0.3 + r() * 0.5 }); }

/* ================= Snow clouds (tileable; moonlit tops, city-lit bellies) ================= */
const CLOUD_DEFS = [
  { seed: 4, y: 0, h: 60, banks: 3, k: 1, sx: 1.3, pal: { top: '#2d3c5e', body: '#141e35', core: '#111a2f', bot: '#1f2a46', bot2: '#19233c' } },
  { seed: 17, y: 42, h: 62, banks: 4, k: 2, sx: 1.25, pal: { top: '#3c4c6e', body: '#1a2641', core: '#162039', bot: '#3a405c', bot2: '#2a324f' } },
  { seed: 31, y: 90, h: 56, banks: 4, k: 3, sx: 1.2, pal: { top: '#4a587a', body: '#253049', core: '#202943', bot: '#6c5d69', bot2: '#4f4a60' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = r() * W, bw = 70 + r() * 110, base = h * (0.45 + r() * 0.35);
    const np = 6 + Math.floor(r() * 6);
    for (let i = 0; i < np; i++) {
      const rx = (7 + r() * 13) * def.sx, ry = 3.5 + r() * 6;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y;
      let v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (y > p.base) v -= (y - p.base) * 0.4;             // flat bellies
      if (v > best) best = v;
    }
    best += 0.14 * Math.sin(TAU * 9 * x / W + y * 0.37 + def.seed) * Math.sin(TAU * 4 * x / W - y * 0.21);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = painter(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.put(x, y, col);
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= Midtown skyline ================= */
const WARM = ['#ffd58a', '#f4bb6a', '#e89e56'], COOL = ['#d6e4ff', '#adc4ec', '#8ea8d6'];
const winCol = r => r() < 0.6 ? WARM[Math.floor(r() * 3)] : COOL[Math.floor(r() * 3)];
// near-layer towers: [x0, width, top, style] (the left edge, x < 59, is Lower Manhattan: see below)
const BLOCKS = [
  [98, 14, 130, 'flat'], [113, 12, 110, 'pyramid'], [126, 11, 136, 'flat'], [139, 11, 100, 'slender'],
  [152, 12, 142, 'flat'], [209, 13, 120, 'crown'], [224, 12, 142, 'flat'], [267, 14, 106, 'slant'],
  [282, 13, 132, 'flat'], [320, 14, 114, 'spire'], [336, 12, 134, 'flat'], [350, 24, 118, 'step'],
];
const AVI = [];                     // blinking red aviation lights { x, y, per, ph }
function drawBlock(x, r, b, addWin) {
  const [x0, w, top, style] = b, x1 = x0 + w - 1, mid = x0 + (w >> 1);
  const BODY = '#0b1426', EDGE = '#1d2b48', TOPC = '#2c3c62';
  const box = (bx, by, bw, bh) => { rect(x, bx, by, bw, bh, BODY); rect(x, bx, by, 1, bh, EDGE); rect(x, bx, by, bw, 1, TOPC); };
  box(x0, top, w, SHORE - top);
  if (style === 'step') {
    box(x0 + 2, top - 6, w - 4, 6); box(x0 + 4, top - 11, w - 8, 5);
    rect(x, mid, top - 16, 1, 5, '#27324c'); AVI.push({ x: mid, y: top - 17, per: 3, ph: r() });
  } else if (style === 'antenna') {
    box(x0 + 3, top - 4, w - 6, 4);
    rect(x, mid, top - 18, 1, 14, '#2a3450'); AVI.push({ x: mid, y: top - 19, per: 2, ph: r() });
  } else if (style === 'pyramid') {                         // gilded pyramid roof with a lantern
    const hw = w >> 1;
    for (let k = 0; k < hw; k++) {
      const y = top - 1 - k, wk = w - 2 * k;
      for (let i = 0; i < wk; i++) px(x, x0 + k + i, y, i < wk / 2 ? (k & 1 ? '#f4cc76' : '#e6b65a') : (k & 1 ? '#b98a40' : '#a47634'));
    }
    rect(x, mid - 1, top - hw - 3, 2, 3, '#fff0b8');
  } else if (style === 'slender') {                          // pencil tower with a lit crown
    for (let wy = top + 1; wy < top + 4; wy++) for (let wx = x0 + 1; wx < x1; wx++) px(x, wx, wy, wy === top + 2 ? '#e6eeff' : '#9fb4dc');
    rect(x, mid, top - 12, 1, 12, '#2a3450'); AVI.push({ x: mid, y: top - 13, per: 2.5, ph: r() });
  } else if (style === 'crown') {
    for (let wx = x0 + 1; wx < x1; wx++) { px(x, wx, top + 2, '#cfe0ff'); px(x, wx, top + 4, '#8fa8d6'); }
    box(x0 + 3, top - 5, w - 6, 5); rect(x, mid, top - 12, 1, 7, '#2a3450');
    AVI.push({ x: mid, y: top - 13, per: 3, ph: r() });
  } else if (style === 'slant') {                            // roof sliced at an angle, lit face
    for (let i = 0; i < w; i++) {
      const yt = top - Math.round((w - 1 - i) * 0.9);
      rect(x, x0 + i, yt, 1, top - yt + 1, BODY);
      px(x, x0 + i, yt, '#a8bce0'); px(x, x0 + i, yt + 1, '#71849f');
    }
  } else if (style === 'spire') {
    box(x0 + 2, top - 5, w - 4, 5); box(x0 + 4, top - 9, w - 8, 4);
    rect(x, mid, top - 22, 1, 13, '#8a96b4'); px(x, mid, top - 22, '#dfe6f4');
    AVI.push({ x: mid, y: top - 23, per: 2, ph: r() });
  } else if (w > 10) box(x0 + 2 + Math.floor(r() * (w - 8)), top - 3, 4, 3);   // flat roof + mechanical box
  // window grid (a few floors fully lit, office style)
  for (let wy = top + 3; wy < SHORE - 8; wy += 3) {
    const fullFloor = r() < 0.08;
    for (let wx = x0 + 2; wx < x1; wx += 2) {
      if (fullFloor) { px(x, wx, wy, COOL[1]); continue; }
      const v = r();
      if (v < 0.33) { const col = winCol(r); if (r() < 0.2) addWin(wx, wy, col); else px(x, wx, wy, col); }
      else if (v < 0.45) px(x, wx, wy, '#16213b');
    }
  }
}
function renderSkyline() {
  const [c, x] = makeCanvas(W, H), r = rng(7), wins = [];
  const addWin = (wx, wy, col) => wins.push({ id: wins.length, x: wx, y: wy, n: 3 + Math.floor(r() * 9), p: 0.5, off: r(), col });
  const FAR = '#16213a', FAR_T = '#243254';
  // far layer: hazy mid-rise blocks with rooftop water tanks
  let fx = -3;
  while (fx < W) {
    const w = 6 + Math.floor(r() * 12), top = 134 + Math.floor(r() * 32);
    rect(x, fx, top, w, SHORE - top, FAR); rect(x, fx, top, w, 1, FAR_T);
    if (r() < 0.4 && w > 6) {
      const tx = fx + 1 + Math.floor(r() * (w - 5));
      rect(x, tx, top - 5, 3, 3, FAR); px(x, tx + 1, top - 6, FAR);
      rect(x, tx, top - 2, 1, 2, FAR); rect(x, tx + 2, top - 2, 1, 2, FAR);
    }
    for (let wy = top + 2; wy < SHORE - 2; wy += 3) for (let wx = fx + 1; wx < fx + w - 1; wx += 2)
      if (r() < 0.2) px(x, wx, wy, r() < 0.6 ? '#9c7e56' : '#5e6d96');
    fx += w + (r() < 0.25 ? 1 : 0);
  }
  for (const b of BLOCKS) drawBlock(x, r, b, addWin);
  drawESBBody(x, r, addWin);
  drawChryslerShaft(x, r, addWin);
  drawLowerManhattan(x, addWin);
  // Manhattan waterfront: highway, piers and a string of lamps
  rect(x, 0, SHORE - 7, W, 7, '#0a1120'); rect(x, 0, SHORE - 7, W, 1, '#1d2842');
  rect(x, 0, SHORE - 3, W, 1, '#131b2e');
  for (let gx = 4; gx < W; gx += 10) { rect(x, gx, SHORE - 9, 1, 2, '#0a1120'); px(x, gx, SHORE - 10, '#ffd690'); }
  for (let gx = 20; gx < W; gx += 57) rect(x, gx, SHORE - 2, 12, 2, '#070c18');
  return { c, wins };
}

/* ================= Empire State Building ================= */
// Massing after the real tower, [y0, y1, half-width, lighting tier] from the antenna down: the antenna, the
// 102nd-floor drum, the ribbed mooring mast flaring into its corner buttresses, the 86th-floor terrace, the crown's
// 81st and 72nd-floor setbacks, the long shaft, then the 30th, 21st and 6th-floor setbacks. Antenna, mast and crown
// are about the same height, and the shaft below is the longest part.
const ESB_ROWS = [
  [22, 41, 0, 'ant'], [42, 44, 1, 'cap'], [45, 57, 2, 'mast'], [58, 61, 3, 'mast'], [62, 64, 4, 'mast'],
  [65, 74, 7, 'obs'], [75, 86, 9, 'upper'], [87, 142, 11, 'shaft'], [143, 155, 13, 'shaft'], [156, 175, 15, 'shaft'],
  [176, 186, 20, 'shaft'],
];
const esbRow = y => ESB_ROWS.find(([a, b]) => y >= a && y <= b);
const esbHW = y => { const row = esbRow(y); return row ? row[2] : -1; };
const ESB_LIT = 86;                                           // the floodlights reach down to the 72nd floor
function drawESBBody(x, r, addWin) {
  for (let y = 22; y <= 186; y++) {
    const hw = esbHW(y), up = esbHW(y - 1);
    for (let i = ESB_X - hw; i <= ESB_X + hw; i++) {
      const dx = i - ESB_X;
      let col = (dx & 1) ? '#0c1427' : '#1c2844';            // window strips / limestone piers
      if (dx === -hw) col = '#2c3c60'; else if (dx === hw) col = '#0a1120';
      if (Math.abs(dx) > up) col = '#3a4c72';                 // setback ledges
      if (y < 42) col = y < 31 ? '#c8d0e0' : '#8a94ae';       // antenna
      px(x, i, y, col);
    }
  }
  for (let y = ESB_LIT + 3; y < SHORE - 8; y += 2) {
    const hw = esbHW(y);
    for (let i = ESB_X - hw + 1; i < ESB_X + hw; i++) {
      if (((i - ESB_X) & 1) === 0) continue;
      const v = r();
      if (v < 0.3) { const col = winCol(r); if (r() < 0.18) addWin(i, y, col); else px(x, i, y, col); }
    }
  }
}
// crown palettes [dark, mid, light] per lighting state: warm white, holiday red/green, blue
const ESB_STATES = [
  { upper: ['#7c6c5c', '#cdb896', '#fff0d2'], obs: ['#8a7a68', '#e2d0ae', '#fffaf0'], mast: ['#8a7a68', '#e2d0ae', '#fffaf0'], cap: ['#9a8a78', '#efe0c2', '#ffffff'] },
  { upper: ['#5e1216', '#bf2a2c', '#ff6a58'], obs: ['#0c4c24', '#1fa648', '#7cff9c'], mast: ['#5e1216', '#cf302c', '#ff7a68'], cap: ['#9a8a78', '#efe0c2', '#ffffff'] },
  { upper: ['#132c66', '#2c64d8', '#86b4ff'], obs: ['#6a86c0', '#c8dcff', '#ffffff'], mast: ['#183680', '#4684ff', '#b4d6ff'], cap: ['#6a86c0', '#c8dcff', '#ffffff'] },
];
const ESB_CROWN = { x0: ESB_X - 11, y0: 42, w: 23, h: ESB_LIT + 9 - 42 };
function renderESBCrown(st) {
  const p = painter(ESB_CROWN.w, ESB_CROWN.h);
  for (let y = ESB_CROWN.y0; y < ESB_CROWN.y0 + ESB_CROWN.h; y++) {
    const [y0, y1, hw, tier] = esbRow(y), up = esbHW(y - 1);
    const spill = y > ESB_LIT ? 1 - (y - ESB_LIT) / 9 : 1;   // floodlight spill fading down the shaft
    for (let i = ESB_X - hw; i <= ESB_X + hw; i++) {
      const dx = i - ESB_X;
      let l = (dx & 1) ? 0.56 : 0.9;                          // recessed window strips / lit piers
      l *= 1 - 0.38 * (dx + hw) / (2 * hw + 1);               // lit from the left
      l *= 0.74 + 0.26 * (y - y0) / Math.max(1, y1 - y0);     // each tier washed up from the setback below
      if (dx === -hw) l = 1; else if (dx === hw) l = Math.min(l, 0.45);
      if (Math.abs(dx) > up || y === y0 && tier === 'obs') l = 1;   // setback parapets, the 86th-floor terrace
      if (y > ESB_LIT) { if (dx & 1 || l * spill < 0.15 + bayer(i, y) * 0.5) continue; l = 0.3; }
      const pal = st[y > ESB_LIT ? 'upper' : tier];
      p.put(i - ESB_CROWN.x0, y - ESB_CROWN.y0, l > 0.78 ? pal[2] : l > 0.5 ? pal[1] : pal[0]);
    }
  }
  return p.done();
}
// hold 0-70 s warm white, 82-150 s red/green, 162-228 s blue; smooth cross-fades (back to white at 240)
function esbMix(t) {
  if (t < 70) return [0, 1, 0];
  if (t < 82) return [0, 1, smooth(70, 82, t)];
  if (t < 150) return [1, 2, 0];
  if (t < 162) return [1, 2, smooth(150, 162, t)];
  if (t < 228) return [2, 0, 0];
  return [2, 0, smooth(228, 240, t)];
}

/* ================= Chrysler Building ================= */
function drawChryslerShaft(x, r, addWin) {
  const cx = CHR_X;
  for (const [y0, y1, hw] of [[111, 132, 8], [133, 160, 9], [161, 186, 12]]) {
    rect(x, cx - hw, y0, hw * 2 + 1, y1 - y0 + 1, '#0f192d');
    for (let i = cx - hw + 2; i < cx + hw; i += 3) rect(x, i, y0, 1, y1 - y0 + 1, '#142035');
    rect(x, cx - hw, y0, 1, y1 - y0 + 1, '#3c4a6a');
    rect(x, cx + hw, y0, 1, y1 - y0 + 1, '#0a0f1c');
    rect(x, cx - hw, y0, hw * 2 + 1, 1, '#5a6684');
  }
  for (let y = 114; y < SHORE - 8; y += 3) {
    const hw = y <= 132 ? 8 : y <= 160 ? 9 : 12;
    for (let i = cx - hw + 1; i < cx + hw; i += 2) {
      const v = r();
      if (v < 0.3) { const col = winCol(r); if (r() < 0.2) addWin(i, y, col); else px(x, i, y, col); }
    }
  }
  rect(x, cx - 10, 112, 2, 1, '#c8d0e2'); rect(x, cx + 9, 112, 2, 1, '#8a94ae');   // eagle gargoyles
}
const CHR_TIERS = 7;                                          // tier i: half-width 8 - i, stacked 4 px apart
const chrArch = (i, dx) => 107 - 4 * i + Math.round(3 * Math.pow(Math.abs(dx) / (8 - i + 0.5), 2));
function renderChryslerCrown() {
  const ox = CHR_X - 9, oy = 58, p = painter(19, 54), pts = [];
  for (let y = 80; y <= 110; y++) for (let dx = -8; dx <= 8; dx++) {
    let tier = -1;                                            // lowest tier whose arch lies above this pixel
    for (let i = 0; i < CHR_TIERS; i++) if (Math.abs(dx) <= 8 - i && y >= chrArch(i, dx)) { tier = i; break; }
    if (tier < 0) continue;
    const d = y - chrArch(tier, dx), left = dx < 0, edge = Math.abs(dx) === 8 - tier;
    let col, lit = false;
    if (d === 0) col = left ? '#f4f7fc' : dx === 0 ? '#e2e8f2' : '#aab4ca';           // arch rim
    else if (d === 1 && !edge && ((dx + tier) & 1) === 0) { col = '#fff7dc'; lit = true; }  // triangle tips
    else if (d === 2 && !edge) { col = '#ffe7ae'; lit = true; }                        // triangle bases
    else col = left ? (d === 1 ? '#8e99b3' : '#7a86a2') : (d === 1 ? '#606b87' : '#4c5672');
    p.put(dx + 9, y - oy, col);
    if (lit) pts.push([CHR_X + dx, y]);
  }
  for (let y = 60; y < 84; y++) if (!p.alpha(9, y - oy)) p.put(9, y - oy, y < 67 ? '#ffffff' : '#c9d2e4');
  return { c: p.done(), ox, oy, pts };
}

/* ================= Lower Manhattan (the nearer cluster, left of the bridge) ================= */
// From the Brooklyn waterfront the Financial District is under 1 km away, so it stands taller and closer than
// Midtown: One World Trade Center (a tapering glass prism whose chamfered corners make long triangular facets,
// a lit parapet ring and a thin white spire with a beacon), a braced 3 WTC-style tower, a glass 4 WTC-style
// slab, a rippled 8 Spruce-style tower near the bridge and lower office blocks in front, all denser and more
// lit than the hazier Midtown blocks behind the bridge.
const OWT = { x: 38, roof: 50, tip: 18, hwTop: 4, hwBase: 12, apex: 150 };   // apex: where the central facet ends
const owtHW = y => Math.round(OWT.hwTop + (OWT.hwBase - OWT.hwTop) * (y - OWT.roof) / (SHORE - OWT.roof));
const FIDI = [[0, 13, 94, 'glass'], [14, 13, 78, 'braced'], [50, 9, 97, 'ripple']];   // [x0, width, top, style]
const FIDI_FRONT = [[0, 10, 128], [9, 12, 118], [22, 8, 140], [46, 12, 130]];       // low blocks in front
function drawLowerManhattan(x, addWin) {
  const r = rng(41);
  const win = (wx, wy, p, warm) => {                          // FiDi offices: denser, mostly lit warm
    const v = r();
    if (v < p) {
      const col = r() < warm ? WARM[Math.floor(r() * 3)] : COOL[Math.floor(r() * 3)];
      if (r() < 0.2) addWin(wx, wy, col); else px(x, wx, wy, col);
    } else if (v < p + 0.1) px(x, wx, wy, '#16213b');
  };
  const grid = (x0, x1, y0, y1, p, warm) => {
    for (let wy = y0; wy < y1; wy += 3) {
      if (r() < 0.07) { for (let wx = x0; wx <= x1; wx += 2) px(x, wx, wy, WARM[1]); continue; }   // a fully lit floor
      for (let wx = x0; wx <= x1; wx += 2) win(wx, wy, p, warm);
    }
  };
  const box = (x0, y0, w, BODY, EDGE, TOPC) => {
    rect(x, x0, y0, w, SHORE - y0, BODY); rect(x, x0, y0, 1, SHORE - y0, EDGE);
    rect(x, x0 + w - 1, y0 + 1, 1, SHORE - y0 - 1, '#060a14'); rect(x, x0, y0, w, 1, TOPC);
  };
  // back row: 4 WTC-style glass slab, braced 3 WTC-style tower, rippled 8 Spruce-style tower
  for (const [x0, w, top, style] of FIDI) {
    const x1 = x0 + w - 1;
    if (style === 'glass') {                                   // floor-by-floor ribbon glazing
      box(x0, top, w, '#0a152b', '#20304f', '#3a4c74');
      for (let wy = top + 3; wy < SHORE - 8; wy += 3) {
        const a = x0 + 1 + Math.floor(r() * 4), b = x1 - 1 - Math.floor(r() * 4);
        for (let wx = a; wx < b; wx++) if (r() < 0.55) px(x, wx, wy, r() < 0.5 ? COOL[1] : WARM[2]);
      }
    } else if (style === 'braced') {                           // setback crown, diagonal bracing on the shaft
      box(x0, top + 6, w, '#0a152b', '#20304f', '#34466e');
      box(x0 + 2, top + 3, w - 4, '#0a152b', '#20304f', '#34466e');
      box(x0 + 4, top, w - 8, '#0a152b', '#20304f', '#34466e');
      grid(x0 + 2, x1 - 1, top + 9, SHORE - 8, 0.42, 0.55);
      const bay = w - 3;
      for (let y0 = top + 8; y0 + bay < 136; y0 += bay) {
        line(x, x0 + 1, y0, x1 - 1, y0 + bay, '#243458'); line(x, x1 - 1, y0, x0 + 1, y0 + bay, '#243458');
      }
      AVI.push({ x: x0 + 6, y: top - 1, per: 2.5, ph: r() });
    } else {                                                   // steel bays rippling down a stepped crown
      for (let i = x0; i <= x1; i++) {
        const yt = top + (i - x0 < 4 ? 0 : i - x0 < 7 ? 3 : 6);
        rect(x, i, yt, 1, SHORE - yt, i === x0 ? '#2a3a5e' : i === x1 ? '#060a14' : '#101c34');
        px(x, i, yt, '#44587e');
      }
      for (let y = top + 2; y < SHORE - 8; y++) for (const k of [0, 1]) {
        const i = x0 + 2 + k * 4 + Math.round(1.2 * Math.sin(y * 0.55 + k * 2));
        if (i < x1 && y > top + (i - x0 < 4 ? 1 : i - x0 < 7 ? 4 : 7)) px(x, i, y, '#34466c');
      }
      for (let wy = top + 4; wy < SHORE - 8; wy += 3) for (let wx = x0 + 1; wx < x1; wx += 2)
        if (wy > top + 7 || wx < x0 + 4) { if (r() < 0.3) { const col = WARM[Math.floor(r() * 3)]; if (r() < 0.2) addWin(wx, wy, col); else px(x, wx, wy, col); } }
    }
  }
  drawOneWTC(x, r, addWin);
  // low office blocks in front, their tops lit a little brighter
  for (const [x0, w, top] of FIDI_FRONT) {
    box(x0, top, w, '#070e1d', '#26385e', '#43557c');
    grid(x0 + 1, x0 + w - 2, top + 3, SHORE - 8, 0.45, 0.7);
  }
}
function drawOneWTC(x, r, addWin) {
  const { x: cx, roof: R, tip, hwTop, apex } = OWT;
  for (let y = R + 2; y < SHORE; y++) {
    // seen corner-on: a light inverted triangle (the chamfered corner facing Brooklyn) from the full roof width
    // down to a point, between two up-pointing side facets that widen towards the base
    const hw = owtHW(y), f = (y - R) / (SHORE - R), ct = Math.round(hwTop * clamp(1 - (y - R) / (apex - R), 0, 1));
    for (let i = cx - hw; i <= cx + hw; i++) {
      const dx = i - cx;
      let col;
      if (ct > 0 && dx === -ct) col = '#7890c0';              // chamfer edges catching the light
      else if (ct > 0 && dx === ct) col = '#3c5282';
      else if (dx === -hw) col = '#34487a';                   // silhouette edges
      else if (dx === hw) col = '#070d1a';
      else if (dx < -ct) col = bayer(i, y) < 0.3 + 0.4 * f ? '#122038' : '#172846';   // side facets, in shade
      else if (dx > ct) col = '#0a1427';
      else col = bayer(i, y) < 0.35 + 0.4 * f ? '#1e3256' : '#263c64';   // central facet, sky-lit
      px(x, i, y, col);
    }
    if ((y - R) % 3 === 1 && y < SHORE - 8) for (let i = cx - hw + 1; i < cx + hw; i++) {   // office floors
      const dx = i - cx;
      if ((ct > 0 && (dx === -ct || dx === ct)) || ((i - cx) & 1) === 0) continue;
      const v = r(), p = Math.abs(dx) < ct ? 0.3 : 0.2;
      if (v < p) { const col = r() < 0.7 ? COOL[Math.floor(r() * 3)] : WARM[Math.floor(r() * 3)]; if (r() < 0.2) addWin(i, y, col); else px(x, i, y, col); }
    }
  }
  // lit parapet ring, roof deck, spire ring and the thin white spire (its beacon blinks per frame)
  for (let dx = -hwTop; dx <= hwTop; dx++) {
    px(x, cx + dx, R, Math.abs(dx) === hwTop ? '#b4c4e4' : '#f2f6ff');
    px(x, cx + dx, R + 1, Math.abs(dx) === hwTop ? '#5a6e98' : '#9cb0d6');
  }
  rect(x, cx - 2, R - 1, 5, 1, '#2a3656'); rect(x, cx - 1, R - 2, 3, 1, '#c8d4ec');
  for (let y = tip; y < R - 2; y++) px(x, cx, y, y < tip + 12 ? '#c4cee4' : '#eef3ff');
}

/* ================= Brooklyn Bridge ================= */
const TC = { hi: '#dcc6a0', lt: '#b69f80', md: '#8d7965', sh: '#64575a', dk: '#3c3548', foot: '#4e454c', wet: '#282434' };
const A_BOT = DECK + 5 - T_TOP;                               // arch openings reach down to the deck's bottom chord
function archHW(ly) {                                         // half-width of a pointed arch opening at local row ly
  if (ly > A_BOT || ly < 17) return -1;
  if (ly >= 22) return 4;
  const yy = 25 - ly - 0.5;
  return Math.sqrt(81 - yy * yy) - 4.5;
}
const isOpen = (gx, ly) => { const hw = archHW(ly); return hw >= 0 && (Math.abs(gx - 11) <= hw || Math.abs(gx - 26) <= hw); };
// one tower sprite: local gx 0..37 (cornice/footing overhang to -2..39), ly 0 = cornice top; canvas = (gx + 2, ly + 1)
function renderBridgeTower() {
  const w = 42, h = T_BASE - T_TOP + 2, p = painter(w, h);
  const put = (gx, ly, col) => p.put(gx + 2, ly + 1, col);
  const near = (gx, ly, d) => { for (let a = -d; a <= d; a++) for (let b = -d; b <= d; b++) if (isOpen(gx + a, ly + b)) return true; return false; };
  for (let ly = 0; ly <= T_BASE - T_TOP; ly++) {
    const foot = ly >= 90, x0 = foot ? -2 : ly <= 3 ? -1 : 0, x1 = foot ? 39 : ly <= 3 ? 38 : 37;
    for (let gx = x0; gx <= x1; gx++) {
      if (isOpen(gx, ly)) continue;
      let col;
      if (ly <= 3) col = ly === 0 ? TC.hi : ly === 3 ? TC.dk : ((gx & 1) ? TC.lt : TC.md);          // cornice + dentils
      else if (foot) col = ly >= 94 ? TC.wet : ly === 90 ? TC.md : (gx < 5 ? TC.sh : TC.foot);   // footing in the river
      else {
        const inPier = gx <= 6 ? gx : gx >= 31 ? gx - 31 : (gx >= 16 && gx <= 21) ? gx - 16 : -1;
        const pw = gx >= 16 && gx <= 21 ? 6 : 7;
        let v;                                                               // floodlit from the deck lamps
        if (ly < 17) v = 0.5 + 0.14 * (ly - 4) / 12;
        else if (ly <= A_BOT) v = 0.56 + 0.32 * clamp((ly - 17) / 44, 0, 1);
        else v = 0.76 - 0.4 * clamp((ly - 62) / 28, 0, 1);
        if (inPier >= 0 && ly >= 17 && ly <= A_BOT) { if (inPier <= 1) v += 0.2; else if (inPier >= pw - 2) v -= 0.28; }
        else if (ly > A_BOT) v += gx < 19 ? 0.05 : -0.08;
        if (gx === 0) v = 1.1;
        if (gx >= 36) v = 0.3;
        if (ly % 5 === 0) v -= 0.1;                                          // stone courses and staggered joints
        else if ((gx + (Math.floor(ly / 5) & 1) * 3) % 6 === 0) v -= 0.06;
        if (ly >= 5 && ly <= 10 && ((gx >= 8 && gx <= 14) || (gx >= 23 && gx <= 29)))   // recessed panels
          v = (ly === 5 || gx === 8 || gx === 23) ? 0.3 : 0.47;
        col = v > 0.95 ? TC.hi : v > 0.72 + (bayer(gx, ly) - 0.5) * 0.12 ? TC.lt : v > 0.45 ? TC.md : v > 0.26 ? TC.sh : TC.dk;
        if (isOpen(gx + 1, ly)) col = TC.hi;                                 // lit jamb
        else if (isOpen(gx - 1, ly)) col = TC.sh;                            // reveal in shadow
        else if (isOpen(gx, ly + 1)) col = TC.dk;                            // arch soffit
        else if (ly < 24 && near(gx, ly, 2)) col = TC.hi;                    // pointed-arch moulding
        if (ly === 12 || ly === A_BOT + 1) col = TC.hi;                      // string courses
        if (ly === 13) col = TC.sh;
      }
      put(gx, ly, col);
    }
  }
  // snow on the cornice, the upper string course and the roadway ledge
  for (let gx = -1; gx <= 38; gx++) { const h1 = hash(gx, 1, 91); if (h1 < 0.93) put(gx, -1, snowPx(h1, hash(gx, 2, 91))); }
  for (let gx = 0; gx <= 37; gx++) {
    if (hash(gx, 3, 91) < 0.7) put(gx, 11, snowPx(hash(gx, 4, 91), hash(gx, 5, 91)));
    if (!isOpen(gx, A_BOT) && hash(gx, 6, 91) < 0.8) put(gx, A_BOT, snowPx(hash(gx, 7, 91), hash(gx, 8, 91)));
  }
  return p.done();
}
function mainCableY(gx) {
  const [L, R] = TW, y0 = T_TOP + 2;
  if (gx <= L) { const vx = -40, xt = L - 19, u = (gx - vx) / (xt - vx); return DECK - 6 - (DECK - 6 - y0) * u * u; }
  if (gx >= R) { const vx = BX + 66, xt = R + 18, u = (gx - vx) / (xt - vx); return DECK - 6 - (DECK - 6 - y0) * u * u; }
  const a = L + 18, b = R - 19, xm = (a + b) / 2, hs = (b - a) / 2, u = (gx - xm) / hs;
  return DECK - 4 - (DECK - 4 - y0) * u * u;
}
const inTower = gx => TW.some(tc => gx >= tc - 19 && gx <= tc + 18);
const DECK_LAMPS = [];
for (let gx = 8; gx < BX; gx += 18) if (!TW.some(tc => Math.abs(gx - tc) < 24)) DECK_LAMPS.push(gx);
function renderBridgeBack() {
  const [c, x] = makeCanvas(W, H);
  // suspenders
  x.globalAlpha = 0.55;
  for (let gx = 1; gx < BX + 4; gx += 3) {
    if (TW.some(tc => Math.abs(gx - tc) < 22)) continue;
    const cy = Math.round(mainCableY(gx));
    if (DECK - 3 - cy > 0) rect(x, gx, cy + 1, 1, DECK - 3 - cy, '#5a6684');
  }
  // diagonal stays fanning out from each tower top
  x.globalAlpha = 0.34;
  for (const tc of TW) for (const side of [-1, 1]) for (let k = 0; k < 9; k++) {
    const ax = side < 0 ? tc - 19 : tc + 18;
    line(x, ax, T_TOP + 4, ax + side * (9 + k * 8), DECK - 3, '#8290b0');
  }
  x.globalAlpha = 1;
  // main cables: dimmer back pair one pixel above the lit front pair
  for (const [dy, col] of [[-1, '#4c5672'], [0, '#b4bed4']]) {
    let prev = null;
    for (let gx = 0; gx < BX + 4; gx++) {
      if (inTower(gx)) { prev = null; continue; }
      const yy = Math.round(mainCableY(gx)) + dy;
      if (prev === null) px(x, gx, yy, col); else line(x, gx - 1, prev, gx, yy, col);
      prev = yy;
    }
  }
  // deck: the raised pedestrian promenade (handrail, snowy plank walkway on posts) above the roadway rail,
  // the two car lanes, stiffening truss, bottom chord
  const D = DECK;
  rect(x, 0, D - 5, BX + 4, 1, '#3a4462');
  for (let gx = 1; gx < BX + 4; gx += 3) px(x, gx, D - 4, '#2c3550');
  for (let gx = 0; gx < BX + 4; gx++) {
    const h1 = hash(gx, 11, 92);
    px(x, gx, D - 3, h1 < 0.62 ? snowPx(h1 * 1.1, hash(gx, 12, 92)) : hash(gx, 13, 92) < 0.5 ? '#6e5e56' : '#54484a');
    px(x, gx, D - 2, gx % 6 === 2 ? '#4a5672' : '#121828');
  }
  rect(x, 0, D - 1, BX + 4, 1, '#77839f');
  rect(x, 0, D, BX + 4, 2, '#151c2e');
  for (let gx = 0; gx < BX + 4; gx++) for (let k = 0; k < 3; k++)
    px(x, gx, D + 2 + k, ((gx + k) & 3) === 0 || ((gx - k) & 3) === 0 ? '#36435f' : '#0f1626');
  rect(x, 0, D + 5, BX + 4, 1, '#44506e');
  x.globalAlpha = 0.5; rect(x, 0, D + 6, BX + 4, 1, '#050810'); x.globalAlpha = 1;
  for (const lx of DECK_LAMPS) { rect(x, lx, D - 6, 1, 3, '#1a2236'); px(x, lx, D - 7, '#ffe2a0'); }   // promenade lamps
  return c;
}
// people strolling the promenade at 1.6 px/s (one span per loop; a jogger does two), a couple walking together
const PROM = [
  { dir: 1, k: 1, ph: 0.12, head: '#f0cca8', coat: '#b44a4a' }, { dir: 1, k: 1, ph: 0.126, head: '#3a2a28', coat: '#c8a878' },
  { dir: -1, k: 1, ph: 0.45, head: '#e8c4a0', coat: '#7c8cba' }, { dir: -1, k: 1, ph: 0.8, head: '#c83a44', coat: '#dfe4ee' },
  { dir: 1, k: 2, ph: 0.62, head: '#f0cca8', coat: '#5a9ad4' },
];
function drawPromenade(t) {
  const span = BX + 16, s = step(t, 480);
  PROM.forEach((p, i) => {
    const u = frac(p.k * t / LOOP + p.ph), x = p.dir > 0 ? Math.round(-8 + u * span) : Math.round(BX + 8 - u * span);
    px(ctx, x, DECK - 6, p.head); px(ctx, x, DECK - 5, p.coat);
    ctx.fillStyle = '#141820';
    if (((s * p.k + i) & 1) === 0) ctx.fillRect(x, DECK - 4, 1, 1); else { ctx.fillRect(x - 1, DECK - 4, 1, 1); ctx.fillRect(x + 1, DECK - 4, 1, 1); }
  });
}
// tower-top flags waving in the wind
const FLAG = ['BBBRRRR', 'BWBWWWW', 'BBBRRRR', 'WWWWWWW', 'RRRRRRR'];
const FLAG_C = { B: '#2c3c8c', W: '#e6e8f0', R: '#c8303e' };
function drawFlag(tc, t, i) {
  const top = T_TOP - 12;
  rect(ctx, tc, top, 1, 11, '#2a2a36'); px(ctx, tc, top - 1, '#d8c890');
  for (let fx = 0; fx < 7; fx++) {
    const dy = Math.round(wave(t, 180, fx * 0.12 + i * 0.37) * fx / 6 * 1.3);
    for (let fy = 0; fy < 5; fy++) px(ctx, tc + 1 + fx, top + 1 + fy + dy, FLAG_C[FLAG[fy][fx]]);
  }
}
// two-way traffic on the deck: eastbound shows headlights leading, westbound too, both with taillights
const DECK_CARS = [];
{ const r = rng(21); for (let i = 0; i < 18; i++) DECK_CARS.push({ dir: i % 2 ? -1 : 1, n: 5 + Math.floor(r() * 5), ph: r(), cool: r() < 0.35 }); }
function drawDeckCars(t) {
  const span = BX + 24;
  for (const car of DECK_CARS) {
    const u = frac(t * car.n / LOOP + car.ph);
    const x = car.dir > 0 ? Math.round(-12 + u * span) : Math.round(BX + 12 - u * span);
    const y = car.dir > 0 ? DECK + 1 : DECK;
    rect(ctx, x - 1, y, 3, 1, '#070a12');
    px(ctx, x + 2 * car.dir, y, car.cool ? '#e6f0ff' : '#fff2c8');
    px(ctx, x - 2 * car.dir, y, '#ff3a2c');
    ctx.globalAlpha = 0.4; px(ctx, x + 3 * car.dir, y, '#fff2c8'); ctx.globalAlpha = 1;
  }
}
// a police car crossing the bridge with its lights going
const SIREN = { t0: 150, t1: 168 };
function drawSiren(t) {
  if (t < SIREN.t0 || t >= SIREN.t1) return null;
  const x = Math.round(lerp(BX + 10, -24, (t - SIREN.t0) / (SIREN.t1 - SIREN.t0))), y = DECK;
  rect(ctx, x - 2, y, 5, 1, '#0a0e18');
  px(ctx, x - 3, y, '#fff6d8'); px(ctx, x + 3, y, '#ff3a2c');
  const red = (step(t, 960) & 1) === 1;                        // 4 flips per second
  rect(ctx, x - 1, y - 1, 3, 1, red ? '#ff4040' : '#4a7aff');
  halo(red ? HL.sirenR : HL.sirenB, x, y - 1, 0.95);
  halo(red ? HL.sirenR2 : HL.sirenB2, x, y - 1, 0.6);
  return { x, red };
}

/* ================= East River ================= */
const WATER_H = RAIL + 8 - SHORE;                             // the river also shows between the railing bars
const renderWater = () => ditherFill(W, WATER_H, ['#0f1e34', '#0b1729', '#08111f', '#060c17'], (i, y) => y / WATER_H);
const FLOES = [];
{
  const r = rng(33);
  for (let i = 0; i < 13; i++) {
    const d = r();
    FLOES.push({ y: Math.round(SHORE + 7 + d * (RAIL - SHORE - 5)), w: Math.round(3 + d * 8 + r() * 3), big: d > 0.55, k: d < 0.5 ? 1 : 2, ph: r() });
  }
}
function renderFloe(w, big) {
  const p = painter(w, big ? 3 : 2);
  for (let i = 0; i < w; i++) {
    const end = i === 0 || i === w - 1;
    if (!end || w < 5) p.put(i, 0, hash(i, w, 5) < 0.7 ? '#d8e2f2' : '#b4c3dc');
    p.put(i, 1, end ? '#44557a' : '#7788aa');
    if (big && !end) p.put(i, 2, '#23324e');
  }
  return p.done();
}
// river ferry (bow on the left, heading west)
const FERRY_ROWS = [
  '.'.repeat(14) + 'w' + '.'.repeat(23),
  '.'.repeat(14) + 'k' + '.'.repeat(23),
  '.'.repeat(10) + 'k'.repeat(13) + '.'.repeat(15),
  '.'.repeat(10) + 'kyy'.repeat(4) + 'k' + '.'.repeat(15),
  '.'.repeat(6) + 'g' + 'k'.repeat(26) + '.'.repeat(5),
  '.'.repeat(5) + 'kyyyyy'.repeat(4) + 'kyyyk' + '.'.repeat(4),
  '.'.repeat(5) + 'kYYYYY'.repeat(4) + 'kYYYk' + '.'.repeat(4),
  '..' + 'h'.repeat(35) + '.',
  '.h' + 'b'.repeat(35) + 'h',
  'h'.repeat(38),
  '.' + 's'.repeat(36) + '.',
  '..' + 'd'.repeat(34) + '..',
];
const FERRY_KEY = { k: '#1a2030', y: '#ffd98a', Y: '#f2b45a', h: '#e4eaf4', b: '#2f5aa8', s: '#8e9ab2', d: '#161e30', w: '#ffffff', g: '#5aff8a' };
const FERRY = { t0: 86, t1: 134, y: 195 };
// a red harbour tug chugging east (bow on the right)
const TUG_ROWS = [
  '.'.repeat(13) + 'w' + '.'.repeat(10),
  '.'.repeat(13) + 'k' + '.'.repeat(10),
  '.'.repeat(8) + 'rr' + '...kkkk' + '.'.repeat(7),
  '.'.repeat(8) + 'rr' + '...kyyk' + '.'.repeat(7),
  '.'.repeat(8) + 'kk' + '.hhhhhhhh' + '.'.repeat(5),
  '.'.repeat(5) + 'hhhhhhhyyhhyyh' + '.'.repeat(5),
  'kkkkkkkkkkkkkkkkkkkkkkk.',
  'RRRRRRRRRRRRRRRRRRRRRRRR',
  '.RRRRRRRRRRRRRRRRRRRRRR.',
  '..dddddddddddddddddddd..',
];
const TUG_KEY = { w: '#ffffff', k: '#15161e', r: '#c8303a', y: '#ffd98a', h: '#dfe4ee', R: '#9e2a30', d: '#161e30' };
const TUG = { t0: 176, t1: 226, y: 197 };
function drawTug(t) {
  if (t < TUG.t0 || t >= TUG.t1) return;
  const x = Math.round(lerp(-28, BX + 2, (t - TUG.t0) / (TUG.t1 - TUG.t0))), y = TUG.y, wl = y + 9;
  for (let j = 0; j < 7; j++) {
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 0.7) * (0.5 + j * 0.15));
    ctx.globalAlpha = 0.3 * (1 - j / 7);
    ctx.drawImage(TUG_C, 0, 8 - j, 24, 1, x + off, wl + 1 + j, 24, 1);
  }
  const s = step(t, 1440);
  ctx.fillStyle = '#d8e4f4';
  for (let j = 0; j < 10; j++) {
    if (hash(j, s, 57) < 0.35) continue;
    ctx.globalAlpha = 0.8 * (1 - j / 10);
    ctx.fillRect(x - 2 - j * 2 - (hash(j, s, 58) < 0.5 ? 0 : 1), wl - 1 + (j & 1), 1, 1);
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(TUG_C, x, y);
  streak(ctx, t, x + 16, 3, '#ffd27a', 0.3, 23, wl + 1, RAIL + 8);
  halo(HL.ferry, x + 15, y + 4, 0.55);
  if (frac(t / 1.5) < 0.5) halo(HL.white, x + 13, y, 0.8);
}
const ESB_STREAK = ['#ffe6b8', '#ff5a4a', '#4a86ff'];
function drawRiver(t, mix, siren) {
  ctx.drawImage(WATER_C, 0, SHORE);
  reflect(ctx, t, { top: SHORE, rows: WATER_H, squash: 1.45, alpha: 0.5, amp: 0.5, grow: 0.05, w: BX + 2 });
  ctx.globalAlpha = 0.22; rect(ctx, 0, SHORE, BX + 2, WATER_H, '#050e1c'); ctx.globalAlpha = 1;
  rect(ctx, 0, SHORE, BX + 2, 1, '#0a1222');
  // the tower footings stand in the water, with a little foam at their base
  const fy = SHORE - T_TOP + 1, fh = T_BASE - SHORE + 1, fs = step(t, 720);
  for (const tc of TW) {
    ctx.drawImage(TOWER_C, 0, fy, 42, fh, tc - 21, SHORE, 42, fh);
    ctx.fillStyle = '#a6b6d2';
    for (let i = -2; i <= 39; i++) if (hash(i + tc, fs, 51) < 0.28) ctx.fillRect(tc - 19 + i, T_BASE + 1, 1, 1);
  }
  // light streaks: the Empire State crown (in its current colour), Chrysler, tower floodlights, deck lamps
  const [a, b, u] = mix;
  streak(ctx, t, ESB_X, 3, ESB_STREAK[a], 0.42 * (1 - u), 1, SHORE + 1, RAIL + 8);
  if (u > 0) streak(ctx, t, ESB_X, 3, ESB_STREAK[b], 0.42 * u, 1, SHORE + 1, RAIL + 8);
  streak(ctx, t, CHR_X, 2, '#e2eaff', 0.2, 2, SHORE + 1, RAIL + 8);
  streak(ctx, t, OWT.x, 3, '#d8e4ff', 0.2, 7, SHORE + 1, RAIL + 8);     // One WTC's parapet and spire
  streak(ctx, t, MOON.x, 4, '#c8d8ff', 0.16, 3, SHORE + 1, RAIL + 8);
  for (const tc of TW) streak(ctx, t, tc, 8, '#ffc878', 0.34, tc, T_BASE + 2, RAIL + 8);
  DECK_LAMPS.forEach((lx, i) => { if (i % 2 === 0) streak(ctx, t, lx, 1, '#ffd890', 0.32, 5 + i, SHORE + 10, RAIL + 8); });
  if (siren) streak(ctx, t, siren.x, 3, siren.red ? '#ff4a4a' : '#5a86ff', 0.6, 9, SHORE + 2, RAIL + 8);
  // ice floes drifting downstream (wrap happens off-screen / behind the building)
  for (const f of FLOES) ctx.drawImage(f.c, Math.round((1 - frac(f.k * t / LOOP + f.ph)) * (BX + 40)) - 20, f.y);
}
function drawFerry(t) {
  if (t < FERRY.t0 || t >= FERRY.t1) return;
  const x = Math.round(lerp(BX - 2, -42, (t - FERRY.t0) / (FERRY.t1 - FERRY.t0))), y = FERRY.y, wl = y + 11;
  for (let j = 0; j < 9; j++) {                                // rippled reflection
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 0.7) * (0.5 + j * 0.15));
    ctx.globalAlpha = 0.3 * (1 - j / 9);
    ctx.drawImage(FERRY_C, 0, 10 - j, 38, 1, x + off, wl + 1 + j, 38, 1);
  }
  const s = step(t, 1440);                                     // wake foam behind the stern
  ctx.fillStyle = '#d8e4f4';
  for (let j = 0; j < 14; j++) {
    if (hash(j, s, 52) < 0.35) continue;
    ctx.globalAlpha = 0.8 * (1 - j / 14);
    ctx.fillRect(x + 37 + j * 2 + (hash(j, s, 53) < 0.5 ? 0 : 1), wl - 1 + (j & 1), 1, 1);
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(FERRY_C, x, y);
  streak(ctx, t, x + 14, 4, '#ffd27a', 0.34, 21, wl + 1, RAIL + 8);
  streak(ctx, t, x + 27, 4, '#ffd27a', 0.3, 22, wl + 1, RAIL + 8);
  halo(HL.ferry, x + 20, y + 5, 0.85);
  halo(HL.green, x + 6, y + 4, 0.8);
  if (frac(t / 1.5) < 0.5) halo(HL.white, x + 14, y, 0.9);
}

/* ================= Waterfront railing and street ================= */
const tintCache = {};
function tint(col, tcol, a) {
  const k = col + tcol + a;
  if (!tintCache[k]) {
    const A = hex(col), B = hex(tcol);
    tintCache[k] = '#' + [0, 1, 2].map(j => Math.round(A[j] + (B[j] - A[j]) * a).toString(16).padStart(2, '0')).join('');
  }
  return tintCache[k];
}
// light pools baked into snow and slush: [cx, cy, rx, ry, colour, strength]
const POOLS = [
  [LAMP.x, 263, 52, 14, '#ffb866', 0.6], [LAMP.x, 240, 40, 9, '#ffb866', 0.4], [405, 263, 46, 9, '#ffc070', 0.55],
  [281, 258, 32, 8, '#5cff8e', 0.32], [68, 262, 24, 6, '#ffc070', 0.4], [STACK.x, 256, 12, 4, '#ff9a50', 0.25],
];
function lit(i, y, col) {
  for (const [cx, cy, rx, ry, lc, s] of POOLS) {
    const d = Math.hypot((i - cx) / rx, (y - cy) / ry);
    if (d >= 1) continue;
    const q = Math.floor((1 - d) * s * 5 + bayer(i, y)) / 5;
    if (q > 0) col = tint(col, lc, Math.min(0.7, q * 0.8));
  }
  return col;
}
function renderStreet() {
  const p = painter(W, H);
  // far sidewalk: trodden snow with footprints
  for (let y = RAIL + 8; y < ROAD0; y++) for (let i = 0; i < W; i++) {
    const v = (y - RAIL - 8) / (ROAD0 - RAIL - 8);
    let col = v + (bayer(i, y) - 0.5) * 0.6 < 0.5 ? '#c6d2e8' : '#a4b2ce';
    if (y === RAIL + 8) col = '#8e9cba';
    else if (hash(i >> 1, y, 61) < 0.05) col = '#8d9bb8';
    p.put(i, y, lit(i, y, col));
  }
  // roadway: wet asphalt, dark tyre tracks, grey slush ridges, a snowed-over centre line
  for (let y = ROAD0; y < WALK; y++) for (let i = 0; i < W; i++) {
    const yy = y - ROAD0, v = hash(i >> 1, y, 63) * 0.35 + (bayer(i, y) - 0.5) * 0.3;
    const ridge = yy <= 2 || (yy >= 14 && yy <= 16) || yy >= 26;
    const track = (yy >= 9 && yy <= 12) || (yy >= 20 && yy <= 23);
    let col;
    if (ridge) col = v > 0.14 ? '#5e6881' : v > -0.04 ? '#444d64' : '#303849';
    else if (track) col = v > 0.22 ? '#1d2333' : '#10141e';
    else col = v > 0.15 ? '#293041' : '#1d2331';
    if (yy === 15 && ((i >> 2) & 1) && hash(i >> 2, 7, 64) > 0.35) col = '#8c7d44';
    p.put(i, y, lit(i, y, col));
  }
  // near curb and sidewalk
  for (let y = WALK - 1; y < H; y++) for (let i = 0; i < W; i++) {
    let col;
    if (y === WALK - 1) col = '#56607a';
    else if (y === WALK) col = '#8894b0';
    else {
      const v = (y - WALK) / (H - WALK);
      col = Math.abs(v - 0.55) < 0.2 + (bayer(i, y) - 0.5) * 0.2 ? '#a4b1cb' : '#d4dff0';
      if (hash(i >> 1, y, 66) < 0.04) col = '#8290ae';
    }
    p.put(i, y, lit(i, y, col));
  }
  const c = p.done(), x = p.x;
  // waterfront railing: bars, rails, posts, all snow-capped
  const IRON = '#0b0f18', IRON_H = '#34405a';
  rect(x, 0, RAIL, W, 1, IRON); rect(x, 0, RAIL + 4, W, 1, IRON);
  for (let i = 0; i < W; i += 3) rect(x, i, RAIL, 1, 8, IRON);
  for (let i = 1; i < W; i += 9) px(x, i, RAIL, IRON_H);
  rect(x, 0, RAIL + 7, W, 1, '#3c465e');
  for (let i = 0; i < W; i++) { const h1 = hash(i, 5, 65); if (h1 < 0.82) px(x, i, RAIL - 1, snowPx(h1, hash(i, 6, 65))); }
  for (let i = 8; i < W; i += 32) { rect(x, i, RAIL - 2, 2, 10, IRON); px(x, i, RAIL - 1, IRON_H); rect(x, i - 1, RAIL - 3, 4, 1, '#e8f0fc'); }
  return c;
}
function renderStreetFront() {
  const p = painter(W, H);
  // snowbank along the near curb (cleared at the subway stairs and at a crossing)
  for (let i = 0; i < BX; i++) {
    if ((i > SUB.x0 - 3 && i < SUB.x1 + 4) || (i > 112 && i < 128) || (i > STACK.x - 9 && i < STACK.x + 16)) continue;
    const hgt = Math.max(1, Math.round(3.2 + 2 * Math.sin(i * 0.17) + 1.4 * Math.sin(i * 0.43 + 2) + hash(i >> 2, 1, 67) * 1.5));
    for (let k = 0; k <= hgt; k++) {
      const y = WALK - 1 - k;
      const col = k === hgt ? '#f4f8ff' : k === hgt - 1 ? '#dfe8f6' : k <= 1 ? '#6a7690' : bayer(i, y) < 0.5 ? '#b8c6de' : '#9eadca';
      p.put(i, y, lit(i, y, col));
    }
  }
  // manhole cover in a patch of wet slush, the striped steam stack standing over it, a traffic cone
  for (let y = WALK - 4; y <= WALK + 1; y++) for (let i = STACK.x - 9; i <= STACK.x + 9; i++) {
    const d = Math.hypot((i - STACK.x) / 9, (y - WALK + 1.5) / 3);
    if (d < 1) p.put(i, y, d > 0.72 ? '#5a6480' : d > 0.55 ? '#3a3e4a' : ((i + y) & 1) ? '#24262e' : '#30333c');
  }
  for (let y = STACK.top; y <= WALK; y++) {
    const band = Math.floor((y - STACK.top) / 3) & 1;
    for (let s = 0; s < 5; s++) p.put(STACK.x - 2 + s, y, band ? ['#ffffff', '#ececf2', '#d8dae4', '#b0b4c6', '#868aa0'][s] : ['#ffb070', '#ff7a1e', '#ee6a16', '#c05014', '#8c3a10'][s]);
  }
  for (let s = -3; s <= 3; s++) p.put(STACK.x + s, STACK.top - 1, Math.abs(s) <= 1 ? '#2a1c1c' : s < 0 ? '#ffb070' : '#b84c12');
  ['..o..', '..w..', '.ooo.', '.www.', 'ooooo', 'kkkkk'].forEach((row, j) => { for (let i = 0; i < 5; i++) {
    const col = { o: '#ff7a1e', w: '#f4f4f4', k: '#3a2a2a' }[row[i]]; if (col) p.put(STACK.x + 7 + i, WALK - 5 + j, col); } });
  // coffee cart: striped awning, steel body with a lit serving window, wheels, stove pipe
  const cx0 = CART.x, cy0 = CART.y;
  for (let y = cy0; y <= cy0 + 4; y++) for (let i = cx0 - 2; i <= cx0 + 38; i++) {
    if (y === cy0 + 4 && (i - cx0 + 2) % 4 >= 2) continue;
    const blue = (Math.floor((i - cx0 + 2) / 4) & 1) === 0;
    p.put(i, y, blue ? (y === cy0 ? '#4a7ad4' : '#2c54aa') : (y === cy0 ? '#f6f8fc' : '#d6dce8'));
  }
  for (let y = cy0 + 5; y <= cy0 + 20; y++) for (let i = cx0; i <= cx0 + 36; i++) {
    let col = (i - cx0) % 4 === 0 ? '#c4cddb' : (i - cx0) % 4 === 3 ? '#7a869c' : '#9ba7bb';
    if (i === cx0) col = '#e2e8f2'; else if (i === cx0 + 36) col = '#5a6478';
    if (y === cy0 + 20) col = '#485066';
    p.put(i, y, col);
  }
  for (let y = cy0 + 7; y <= cy0 + 14; y++) for (let i = cx0 + 5; i <= cx0 + 29; i++) {
    const edge = y === cy0 + 7 || i === cx0 + 5 || i === cx0 + 29;
    p.put(i, y, edge ? '#3a3a48' : y < cy0 + 10 ? '#ffe2a0' : '#f6bf6e');
  }
  ['..kkk..', '.kkkkk.', '.fefef.', '..fff..', '.jjjjj.', 'jjjJjjj', 'jjjJjjj'].forEach((row, j) => { for (let i = 0; i < 7; i++) {
    const col = { k: '#2c3350', f: '#e0a47c', e: '#3a2418', j: '#a83232', J: '#e8e0d0' }[row[i]]; if (col) p.put(cx0 + 15 + i, cy0 + 8 + j, col); } });
  for (let y = cy0 + 9; y <= cy0 + 14; y++) for (let i = 0; i < 4; i++) p.put(cx0 + 7 + i, y, y === cy0 + 9 ? '#5a6072' : ['#e2e8f0', '#b8c0ce', '#8a94a8', '#6a7284'][i]);
  for (let i = cx0 + 4; i <= cx0 + 30; i++) p.put(i, cy0 + 15, '#e6ecf4');
  for (const cxp of [cx0 + 8, cx0 + 11, cx0 + 25]) { p.put(cxp, cy0 + 14, '#ffffff'); p.put(cxp, cy0 + 13, '#f0f0f0'); }
  for (const wx of [cx0 + 7, cx0 + 29]) ['.sss.', 'smmms', 'smkms', 'smmms', '.sss.'].forEach((row, j) => { for (let i = 0; i < 5; i++)
    if (row[i] !== '.') p.put(wx - 2 + i, cy0 + 20 + j, { s: '#15151c', m: '#6a7082', k: '#2a2a36' }[row[i]]); });
  for (let y = cy0 - 6; y < cy0; y++) { p.put(cx0 + 33, y, '#3a3e4c'); p.put(cx0 + 34, y, '#5a6072'); }
  // roof sign box
  for (let y = cy0 - 8; y <= cy0 - 1; y++) for (let i = cx0 + 4; i <= cx0 + 30; i++)
    p.put(i, y, y === cy0 - 8 || y === cy0 - 1 || i === cx0 + 4 || i === cx0 + 30 ? '#5a3a22' : '#fff0c4');
  // subway entrance: steps dropping into a warm-lit stairwell, green railing, sign, globe lamps on posts
  const { x0: s0, x1: s1 } = SUB, smid = (s0 + s1) >> 1;
  for (let y = WALK + 1; y <= WALK + 11; y++) for (let i = s0 + 3; i <= s1 - 2; i++) {
    const k = y - WALK - 1, deep = k / 10;
    p.put(i, y, i === s0 + 3 || i === s1 - 2 ? '#0c1016' : k % 2 === 0 ? tint('#4a5264', '#ffc080', deep * 0.55) : tint('#10141c', '#6a4a30', deep * 0.75));
  }
  for (let i = s0 + 3; i <= s1 - 2; i++) { const h1 = hash(i, 11, 68); if (h1 < 0.9) p.put(i, WALK, snowPx(h1, hash(i, 12, 68))); }
  for (let i = s0; i <= s1 + 1; i++) {
    p.put(i, WALK - 9, '#1e4e30'); p.put(i, WALK - 5, '#173a24'); p.put(i, WALK - 1, '#12301e');
    const h1 = hash(i, 9, 68); if (h1 < 0.85) p.put(i, WALK - 10, snowPx(h1, hash(i, 10, 68)));
  }
  for (let i = s0; i <= s1 + 1; i += 2) for (let y = WALK - 8; y < WALK - 1; y++) if (y !== WALK - 5) p.put(i, y, '#173a24');
  for (let y = WALK - 19; y <= WALK - 11; y++) for (let i = smid - 13; i <= smid + 14; i++)
    p.put(i, y, y === WALK - 19 || y === WALK - 11 || i === smid - 13 || i === smid + 14 ? '#0e2616' : '#1d4c2f');
  for (let i = smid - 13; i <= smid + 14; i++) { const h1 = hash(i, 13, 68); if (h1 < 0.8) p.put(i, WALK - 20, snowPx(h1, hash(i, 14, 68))); }
  for (const sx of [smid - 10, smid + 11]) p.put(sx, WALK - 10, '#0e2616');
  for (const sx of [s0, s1]) {
    for (let y = 240; y <= WALK + 8; y++) { p.put(sx, y, '#2c5a3a'); p.put(sx + 1, y, '#12301e'); }
    for (let i = -1; i <= 2; i++) { p.put(sx + i, WALK + 8, '#12301e'); p.put(sx + i, 239, '#12301e'); }
    ['.ggg.', 'gGGGg', 'gGWGg', 'gGGGg', '.ggg.'].forEach((row, j) => { for (let i = 0; i < 5; i++)
      if (row[i] !== '.') p.put(sx - 2 + i + 0.5, 234 + j, { g: '#2a8a4a', G: '#5ada82', W: '#d0ffe0' }[row[i]]); });
  }
  // fire hydrant with a snow cap
  ['..nnn..', '..rrr..', '.rrrrr.', 'rrRrrrr', '.rRrrr.', '.rRrrr.', '.rRrrr.', 'rrrrrrr', 'kkkkkkk'].forEach((row, j) => { for (let i = 0; i < 7; i++) {
    const col = { n: '#eef4ff', r: '#c42a2a', R: '#ff6a58', k: '#5a1414' }[row[i]]; if (col) p.put(336 + i, WALK - 1 + j, col); } });
  // bishop's-crook lamp post
  const PX = 20, POST = '#0f1a18', POSTH = '#34504a';
  for (let y = 144; y < 266; y++) {
    const wd = y >= 258 ? 6 : y >= 252 ? 4 : 2, x0 = PX + 1 - (wd >> 1);
    for (let i = 0; i < wd; i++) p.put(x0 + i, y, i === 0 ? POSTH : POST);
  }
  for (const ry of [196, 222, 246]) for (let i = -1; i <= 2; i++) p.put(PX + i, ry, i === -1 ? POSTH : POST);
  for (let a = 0; a <= 180; a += 2) {
    const ang = a * Math.PI / 180, ax = 27.5 - 6.5 * Math.cos(ang), ay = 145.5 - 7 * Math.sin(ang);
    p.put(ax, ay, POST); p.put(ax, ay + 1, POST);
    if (a > 20 && a < 160 && hash(a, 1, 69) < 0.8) p.put(ax, ay - 1, snowPx(hash(a, 2, 69), hash(a, 3, 69)));
  }
  for (const [sx, sy] of [[24, 146], [25, 145], [26, 145], [27, 146], [26, 147], [23, 147]]) p.put(sx, sy, POST);
  const LX = LAMP.x;
  p.put(LX, 146, POST);
  for (let i = -1; i <= 1; i++) p.put(LX + i, 147, POST);
  for (let i = -2; i <= 2; i++) p.put(LX + i, 148, POST);
  for (const [i, y] of [[-1, 146], [1, 146], [-2, 147], [2, 147]]) p.put(LX + i, y, '#e8f0fc');
  for (let y = 149; y <= 154; y++) for (let i = -2; i <= 2; i++)
    p.put(LX + i, y, Math.abs(i) === 2 ? POST : y < 152 ? '#fff6d6' : '#ffd890');
  for (let i = -1; i <= 1; i++) p.put(LX + i, 155, POST);
  p.put(LX, 156, POST);
  const c = p.done(), x = p.x;
  text(x, 'COFFEE', cx0 + 6, cy0 - 6, '#7a3616');
  text(x, 'UPTOWN', smid - 10, WALK - 17, '#eef4ec');
  return c;
}

/* ================= Brick walk-up with fire escapes and a rooftop water tower (right) ================= */
const COLX = [380, 408, 443, 470];
const FLOORY = [74, 110, 146, 182];
const WIN_W = 14, WIN_H = 22;
const FE = { x0: 376, x1: 429 };                              // fire escape spans columns 1-2
const WKIND = [['toggle', 'blinds', 'dim', 'toggle'], ['warm', 'toggle', 'tree', 'warm'],
  ['tv', 'warm', 'toggle', 'dim'], ['toggle', 'warm', 'plant', 'toggle']];
const BWIN = [];
{
  const r = rng(505);
  FLOORY.forEach((y, fi) => COLX.forEach((x, ci) => BWIN.push({ id: BWIN.length, x, y, w: WIN_W, h: WIN_H,
    kind: WKIND[fi][ci], n: 3 + Math.floor(r() * 5), p: 0.55, off: r() })));
}
const glassOf = w => [w.x + 1, w.y + 1, w.w - 2, w.h - 2];
function sash(c, w, col) {
  const [gx, gy, gw, gh] = glassOf(w);
  c.fillStyle = col;
  c.fillRect(gx, gy + (gh >> 1), gw, 1);                      // meeting rail of the double-hung sash
  c.fillRect(gx + (gw >> 1), gy, 1, gh);
}
const ROOF = 56;                                              // top of the cornice
const TANK = { cx: 452, top: 20, bot: 42 };                   // water tower barrel rows
const CHIM = { x: 394, y: 38 };                               // chimney mouth
const PIGEONS = [399, 405, 411];
function renderBldBack() {
  const p = painter(W, H);
  const WB = ['#7c4434', '#6e3b2e', '#603328'], CB = ['#5a3230', '#4e2b2b', '#432527'];
  // brick facade: cool near the roof, warmed by the street below; stone quoins on the corner
  for (let y = ROOF + 7; y < WALK; y++) for (let i = BX; i < W; i++) {
    const yy = y - ROOF - 7, row = Math.floor(yy / 3), off = (row & 1) * 3;
    const warm = clamp((y - 80) / 160, 0, 1) > bayer(i, y);
    let col;
    if (yy % 3 === 2 || (i - BX + off) % 6 === 5) col = warm ? '#3e2320' : '#311c1e';
    else { const v = hash(Math.floor((i - BX + off) / 6), row, 7); col = (warm ? WB : CB)[v < 0.25 ? 0 : v < 0.8 ? 1 : 2]; }
    const q = i - BX;
    if (q < 5) { const len = (Math.floor(yy / 6) & 1) ? 3 : 5; if (q < len) col = yy % 6 === 5 ? '#4a3a34' : q === 0 ? '#bca68c' : '#9a8468'; }
    p.put(i, y, col);
  }
  // cornice with dentils and brackets; snow piled on top, a few icicles
  const CORN = ['#b39478', '#8f7059', '#6d5243', null, '#5c4438', '#4a362e', '#2e2020'];
  for (let i = BX - 3; i < W; i++) CORN.forEach((col, k) => p.put(i, ROOF + k, col || ((i & 1) ? '#8f7059' : '#3a2a26')));
  for (let i = BX + 2; i < W; i += 9) for (let k = 0; k < 4; k++) { p.put(i, ROOF + 7 + k, k === 3 ? '#4a362e' : '#9a7a62'); p.put(i + 1, ROOF + 7 + k, '#5c4438'); }
  for (let i = BX - 3; i < W; i++) {
    const hgt = 2 + Math.round(hash(i >> 1, 1, 81) * 1.4);
    for (let k = 1; k <= hgt; k++) p.put(i, ROOF - k, k === hgt ? '#f2f6ff' : snowPx(hash(i, k, 82), hash(i, k, 83)));
    if (hash(i, 2, 81) < 0.1 && (i - BX - 2) % 9 > 1) {
      const len = 1 + Math.floor(hash(i, 3, 81) * 4);
      for (let k = 0; k < len; k++) p.put(i, ROOF + 7 + k, k === len - 1 ? '#8ea4c8' : '#cfe0f4');
    }
  }
  // chimney
  for (let y = CHIM.y + 2; y < ROOF - 1; y++) for (let i = CHIM.x - 4; i <= CHIM.x + 3; i++)
    p.put(i, y, i === CHIM.x + 3 ? '#2a1818' : (y % 3 === 0 || (i + (Math.floor(y / 3) & 1) * 2) % 4 === 0) ? '#3a2220' : '#5e3228');
  for (let i = CHIM.x - 5; i <= CHIM.x + 4; i++) { p.put(i, CHIM.y + 1, '#6a5a52'); p.put(i, CHIM.y, snowPx(hash(i, 1, 84), hash(i, 2, 84))); }
  p.put(CHIM.x - 1, CHIM.y + 1, '#1a1010'); p.put(CHIM.x, CHIM.y + 1, '#1a1010');
  // water tower: steel legs on roof beams, wooden barrel with iron hoops, snow-capped cone, icicles, ladder
  const { cx, top, bot } = TANK;
  for (const lx of [cx - 11, cx - 1, cx + 9]) for (let y = bot + 3; y < ROOF - 1; y++) { p.put(lx, y, '#16161f'); p.put(lx + 1, y, '#2c2c3a'); }
  for (let i = cx - 13; i <= cx + 12; i++) { p.put(i, bot + 1, '#262634'); p.put(i, bot + 2, '#16161f'); }
  const bw = y => (y - top) >= 11 ? 1 : 0;
  for (let y = top; y <= bot; y++) {
    const x0 = cx - 12 - bw(y), x1 = cx + 11 + bw(y);
    for (let i = x0; i <= x1; i++) {
      const u = (i - x0) / (x1 - x0);
      let col = u < 0.18 ? '#8e6a4c' : u < 0.55 ? '#72503a' : u < 0.82 ? '#5a3d2d' : '#432c22';
      if ((i - x0) % 3 === 0) col = u < 0.5 ? '#5e4230' : '#3a261e';
      p.put(i, y, col);
    }
  }
  for (const hy of [top + 3, top + 9, top + 15, bot - 1])
    for (let i = cx - 12 - bw(hy); i <= cx + 11 + bw(hy); i++) p.put(i, hy, i < cx - 8 ? '#7a7a8a' : '#26222a');
  for (let y = top - 11; y < top - 1; y++) {
    const hw = Math.round((y - (top - 11)) * 13 / 11);
    for (let i = cx - hw; i <= cx + hw - 1; i++) {
      const u = (i - (cx - hw)) / Math.max(1, 2 * hw);
      p.put(i, y, u < 0.35 ? '#eef4ff' : u < 0.7 ? '#c6d4ec' : '#93a4c6');
    }
  }
  for (let i = cx - 14; i <= cx + 13; i++) { p.put(i, top - 1, '#5a4030'); if (hash(i, 5, 85) < 0.18) { p.put(i, top, '#cfe0f4'); if (hash(i, 6, 85) < 0.5) p.put(i, top + 1, '#9fb4d6'); } }
  p.put(cx - 1, top - 13, '#2a2a36'); p.put(cx - 1, top - 12, '#2a2a36');
  for (let y = top - 1; y < ROOF - 1; y++) { p.put(cx - 16, y, '#1c1c26'); p.put(cx - 14, y, '#1c1c26'); if (y % 3 === 0) p.put(cx - 15, y, '#1c1c26'); }
  // windows: stone lintels and sills (snow on both), dark frames, unlit glass reflecting the sky
  for (const w of BWIN) {
    const { x: wx, y: wy } = w;
    for (let i = wx - 2; i < wx + WIN_W + 2; i++) {
      p.put(i, wy - 4, '#b09878'); p.put(i, wy - 3, '#927a60'); p.put(i, wy - 2, '#6e5a48'); p.put(i, wy - 1, '#2a1a1a');
      const h1 = hash(i, wy, 3); if (h1 < 0.88) p.put(i, wy - 5, snowPx(h1 * 0.9, hash(i, wy, 4)));
    }
    for (let y = wy; y < wy + WIN_H; y++) for (let i = wx; i < wx + WIN_W; i++) {
      const edge = y === wy || y === wy + WIN_H - 1 || i === wx || i === wx + WIN_W - 1;
      const refl = y < wy + 11 && ((i - y + 400) % 9 < 2);
      p.put(i, y, edge ? '#1c1216' : refl ? '#1f2a46' : y < wy + 11 ? '#101728' : '#0b101e');
    }
    for (let i = wx - 1; i < wx + WIN_W + 1; i++) {
      const h1 = hash(i, wy, 5);
      p.put(i, wy + WIN_H, h1 < 0.85 ? snowPx(h1 * 0.85, hash(i, wy, 6)) : '#927a60');
      p.put(i, wy + WIN_H + 1, '#927a60'); p.put(i, wy + WIN_H + 2, '#2a1a1a');
    }
  }
  // ground floor bodega: sign band, lit shop window with shelves, door, side window
  for (let y = 210; y <= 218; y++) for (let i = BX; i < W; i++) p.put(i, y, y === 210 ? '#4c8c66' : y === 218 ? '#0e2418' : '#1c4630');
  for (let i = BX; i < W; i++) { const h1 = hash(i, 7, 86); if (h1 < 0.8) p.put(i, 209, snowPx(h1, hash(i, 8, 86))); }
  for (let y = 219; y < WALK; y++) for (let i = BX; i < W; i++) p.put(i, y, i < BX + 2 ? '#3a2426' : '#2a1a1c');
  for (let y = 221; y <= 251; y++) for (let i = 378; i <= 432; i++) {
    const v = (y - 221) / 31 + (bayer(i, y) - 0.5) * 0.3;
    p.put(i, y, v < 0.35 ? '#ffe2a2' : v < 0.7 ? '#f7c070' : '#e6a258');
  }
  for (const sy of [230, 238, 246]) {
    for (let i = 379; i <= 431; i++) p.put(i, sy, '#8a5a36');
    for (let i = 380; i <= 430; i++) if (hash(i, sy, 71) < 0.72) {
      const hgt = 1 + Math.floor(hash(i >> 1, sy, 72) * 4), col = ['#c83a3a', '#3a8a4a', '#f2d060', '#4a6ac8', '#e87a3a', '#f4f0e0'][Math.floor(hash(i >> 1, sy, 73) * 6)];
      for (let k = 1; k <= hgt; k++) p.put(i, sy - k, col);
    }
  }
  for (let y = 221; y <= 251; y++) p.put(405, y, '#2a1a1c');
  ['.X...X.', '.XXXXX.', 'XXXXXXX', 'XXXXXXXX'].forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === 'X') p.put(418 + i, 248 + j, '#1a1210'); });
  for (let y = 222; y < WALK; y++) for (let i = 438; i <= 452; i++) {
    const edge = i === 438 || i === 452 || y === 222;
    p.put(i, y, edge ? '#140c0e' : (y < 244 && i > 440 && i < 450) ? (y < 232 ? '#ffe0a0' : '#f2b868') : '#3a2224');
  }
  p.put(449, 246, '#d8b060');
  for (let a = 0; a < 20; a++) { const ang = a / 20 * TAU; p.put(445 + Math.round(Math.cos(ang) * 3), 229 + Math.round(Math.sin(ang) * 3), a % 3 ? '#1f5a30' : '#3a8a4a'); }
  for (const [bx, by] of [[445, 232], [444, 233], [446, 233], [445, 226]]) p.put(bx, by, '#d83040');
  for (let y = 221; y <= 251; y++) for (let i = 458; i < W; i++) p.put(i, y, i === 458 ? '#140c0e' : (y - 221) / 31 + (bayer(i, y) - 0.5) * 0.3 < 0.5 ? '#f8d08a' : '#e8a860');
  const c = p.done(), x = p.x;
  // mullions of the unlit windows
  for (const w of BWIN) sash(x, w, '#1c1216');
  text(x, 'DELI & GROCERY', 384, 212, '#f8ecd0');
  // water tower bracing
  line(x, TANK.cx - 10, TANK.bot + 3, TANK.cx - 1, ROOF - 3, '#22222e'); line(x, TANK.cx - 1, TANK.bot + 3, TANK.cx - 10, ROOF - 3, '#22222e');
  line(x, TANK.cx, TANK.bot + 3, TANK.cx + 9, ROOF - 3, '#22222e'); line(x, TANK.cx + 9, TANK.bot + 3, TANK.cx, ROOF - 3, '#22222e');
  return c;
}
function stair(x, sx, sy, ex, ey, seed) {
  const IRON = '#0a0a10', n = Math.floor((ey - sy) / 3), dir = ex > sx ? 1 : -1;
  line(x, sx, sy, ex, ey, IRON);                               // stringer
  line(x, sx, sy - 6, ex, ey - 6, IRON);                       // handrail
  for (let k = 1; k < n; k++) {
    const f = k / n, tx = Math.round(sx + (ex - sx) * f), ty = Math.round(sy + (ey - sy) * f);
    rect(x, dir > 0 ? tx - 3 : tx, ty, 4, 1, IRON);            // treads
    if (hash(k, seed, 25) < 0.7) px(x, tx + (dir > 0 ? -2 : 1), ty - 1, '#e0eaf8');
  }
}
function renderBldFront() {
  const [c, x] = makeCanvas(W, H);
  const IRON = '#0a0a10', IRON_H = '#35353f', { x0, x1 } = FE;
  const land = FLOORY.map(wy => wy + WIN_H + 3);
  land.forEach((ly, fi) => {
    for (const [a, b] of [[x0 + 2, x0 + 7], [x1 - 2, x1 - 7]]) { line(x, a, ly + 2, a, ly + 6, IRON); line(x, a, ly + 6, b, ly + 2, IRON); }
    rect(x, x0, ly, x1 - x0 + 1, 2, IRON);
    for (let i = x0 + 1; i < x1; i += 2) px(x, i, ly + 1, '#1c1c26');
    rect(x, x0, ly - 8, x1 - x0 + 1, 1, IRON); rect(x, x0, ly - 4, x1 - x0 + 1, 1, IRON);
    for (let i = x0; i <= x1; i += 3) rect(x, i, ly - 8, 1, 8, IRON);
    rect(x, x1, ly - 8, 1, 8, IRON);
    for (let i = x0 + 2; i < x1; i += 6) px(x, i, ly - 8, IRON_H);
    for (let i = x0; i <= x1; i++) {
      const a = hash(i, fi, 21); if (a < 0.92) px(x, i, ly - 1, snowPx(a * 0.8, hash(i, fi, 22)));
      const b = hash(i, fi, 23); if (b < 0.62) px(x, i, ly - 9, snowPx(b, hash(i, fi, 24)));
    }
    if (fi < land.length - 1) {
      const right = fi % 2 === 0;
      stair(x, right ? x0 + 4 : x1 - 4, ly + 2, right ? x1 - 10 : x0 + 10, land[fi + 1] - 1, fi);
    }
  });
  // gooseneck ladder from the top landing up over the cornice
  for (let y = ROOF - 2; y < land[0] - 8; y++) { px(x, x0 + 3, y, IRON); px(x, x0 + 6, y, IRON); if (y % 3 === 0) rect(x, x0 + 4, y, 2, 1, IRON); }
  rect(x, x0 + 3, ROOF - 3, 4, 1, IRON);
  return c;
}

/* ----- per-frame window life ----- */
const TVC = ['#3c6cff', '#6d9cff', '#a8ccff', '#2a44b8', '#e2efff', '#5a7ad8'];
const XMAS = ['#ff4a4a', '#5aff7a', '#5a8cff', '#ffd84a', '#ff7ae0', '#ffffff'];
const TREE_HW = [0, 1, 1, 2, 3, 2, 3, 4, 3, 4, 5, 4, 5, 5];
const TREE_LIGHTS = [];
{ const r = rng(78); TREE_HW.forEach((hw, j) => { if (j >= 2 && j % 2 === 0) for (let k = 0; k < 2; k++) TREE_LIGHTS.push([Math.round((r() * 2 - 1) * hw), j]); }); }
function warmGlass(w, curtains) {
  const [gx, gy, gw, gh] = glassOf(w), t3 = Math.floor(gh * 0.35);
  rect(ctx, gx, gy, gw, t3, '#ffdc96');
  rect(ctx, gx, gy + t3, gw, gh - t3, '#f6b862');
  rect(ctx, gx, gy + gh - 3, gw, 3, '#e29a50');
  if (!curtains) return;
  ctx.fillStyle = '#b4523a'; ctx.fillRect(gx, gy, 2, gh); ctx.fillRect(gx + gw - 2, gy, 2, gh);
  ctx.fillStyle = '#d8784a';
  for (let y = gy; y < gy + gh; y += 2) { ctx.fillRect(gx + 1, y, 1, 1); ctx.fillRect(gx + gw - 2, y, 1, 1); }
}
// someone crossing the room behind one window, twice per loop
const SIL = { id: 9, wins: [[44, 52], [172, 180]] };
function drawSilhouette(w, t) {
  for (const [a, b] of SIL.wins) {
    if (t < a || t >= b) continue;
    const [gx, gy, gw, gh] = glassOf(w), u = (t - a) / (b - a);
    const cx = Math.round(gx - 5 + u * (gw + 10)), bob = Math.round(Math.abs(Math.sin(u * Math.PI * 6)));
    ctx.fillStyle = '#4a2416';
    for (const [x0, y0, ww, hh] of [[cx - 2, gy + 5 - bob, 4, 4], [cx - 1, gy + 9 - bob, 2, 1], [cx - 4, gy + 10 - bob, 8, 12]]) {
      const xa = Math.max(gx, x0), xb = Math.min(gx + gw, x0 + ww), ya = Math.max(gy, y0), yb = Math.min(gy + gh, y0 + hh);
      if (xb > xa && yb > ya) ctx.fillRect(xa, ya, xb - xa, yb - ya);
    }
  }
}
function drawWarm(w, I, t = 0) {
  warmGlass(w, true);
  if (w.id === SIL.id) drawSilhouette(w, t);
  sash(ctx, w, '#5a2e1a');
  const [gx, gy, gw, gh] = glassOf(w);
  if (I < 1) rect(ctx, gx, gy, gw, gh, `rgba(40,14,8,${(1 - I).toFixed(3)})`);
  halo(HL.win, w.x + w.w / 2, w.y + w.h / 2, 0.8 * I);
}
function drawXmasTree(w, t) {
  warmGlass(w, false);
  const cx = w.x + 7, ty = w.y + 4;
  TREE_HW.forEach((hw, j) => rect(ctx, cx - hw, ty + j, hw * 2 + 1, 1, j % 3 === 1 ? '#23603a' : '#16482a'));
  rect(ctx, cx - 1, ty + 14, 3, 3, '#6a3a22');
  px(ctx, cx, ty - 1, hash(1, step(t, 240), 43) < 0.8 ? '#fff4a0' : '#c8a040');
  const s = step(t, 480);
  TREE_LIGHTS.forEach(([dx, dy], i) => {
    if (hash(i, s, 41) < 0.22) return;
    px(ctx, cx + dx, ty + dy, XMAS[Math.floor(hash(i, s >> 2, 42) * XMAS.length)]);
  });
  sash(ctx, w, '#5a2e1a');
  halo(HL.win, cx, w.y + 11, 0.8);
  halo(HL.tree, cx, ty + 8, 0.75 + 0.25 * wave(t, 120));
}
function drawTV(w, t) {
  const [gx, gy, gw, gh] = glassOf(w), s = step(t, 1200), I = 0.45 + 0.55 * hash(13, s);
  rect(ctx, gx, gy, gw, gh, '#0a0e22');
  ctx.globalAlpha = I * 0.7; rect(ctx, gx, gy, gw, gh, TVC[Math.floor(hash(12, s) * TVC.length)]); ctx.globalAlpha = 1;
  sash(ctx, w, '#0c1028');
  halo(HL.tv, gx + gw / 2, gy + gh / 2, I);
}
function drawBlinds(w) {
  const [gx, gy, gw, gh] = glassOf(w);
  rect(ctx, gx, gy, gw, gh, '#ffd690');
  ctx.globalAlpha = 0.5; ctx.fillStyle = '#8a4a1e';
  for (let y = gy + 1; y < gy + gh; y += 2) ctx.fillRect(gx, y, gw, 1);
  ctx.globalAlpha = 1;
  sash(ctx, w, '#5a2e1a');
  halo(HL.win, w.x + w.w / 2, w.y + w.h / 2, 0.7);
}
function drawPlant(w) {
  warmGlass(w, true);
  const bx = w.x + 4, by = w.y + w.h - 2;
  rect(ctx, bx + 1, by - 3, 5, 3, '#a0502e');
  for (const [dx, dy] of [[3, -4], [2, -5], [4, -5], [1, -6], [3, -6], [5, -6], [0, -7], [2, -8], [4, -7], [6, -8], [3, -9], [1, -9]])
    px(ctx, bx + dx, by + dy, dy < -7 ? '#3a7a3a' : '#23582a');
  sash(ctx, w, '#5a2e1a');
  halo(HL.win, w.x + w.w / 2, w.y + w.h / 2, 0.8);
}
const NEON_BUZZ = [[33, 34.6], [141, 142.2], [206.5, 208.3]];
/* hidden things: the snowman on the roof, the rat and its slice on a lintel, the sleigh over the moon */
const SNOWMAN_ROWS = ['..hhh..', '.hhhhh.', '..wwb..', '..kwk..', '..wob..', 't.sss.t', '.twwbt.', '.wwkwb.', 'wwwwwbb', 'wwwkwbb', '.wwwbb.'];
const SNOWMAN_KEY = { h: '#23232e', w: '#eef4ff', b: '#b4c4e2', k: '#16161e', o: '#ff9a3a', s: '#c2343c', t: '#4a3a30' };
const RAT_KEY = { c: '#b47a36', y: '#e6b648', o: '#c4402c', e: '#9a8088', k: '#0e0c10', n: '#c88a90', b: '#6e6a74', d: '#4e4a56', t: '#a8848c', f: '#3a343c' };
const SLICE_ROWS = ['cyy..', 'cyoyy', 'cyy..'], RAT_ROWS = ['.ee....', 'ekbbb..', 'nbbbbb.', '.bbddbt', '.f..f.tt'];
// a reindeer and the sleigh behind it, heading left: only what is in front of the moon shows, dark against it
const SLEIGH_ROWS = ['.#.#.......', '##......#..', '.####..###.', '.#..#.####.', '.#..#.#..##'];
function drawSleigh(t) {
  const w = SLEIGH.find(([a, b]) => t >= a && t < b);
  if (!w) return;
  const u = (t - w[0]) / (w[1] - w[0]), x0 = Math.round(lerp(MOON.x + 8, MOON.x - 8 - SLEIGH_ROWS[0].length, u)), y0 = MOON.y - 3 + Math.round(2 * Math.sin(Math.PI * u));
  ctx.fillStyle = '#141c30';
  SLEIGH_ROWS.forEach((row, j) => { for (let i = 0; i < row.length; i++) {
    if (row[i] === '#' && Math.hypot(x0 + i - MOON.x, y0 + j - MOON.y) < 7.3) ctx.fillRect(x0 + i, y0 + j, 1, 1);
  } });
}
function drawRat(t) {
  const tug = frac(t / 3) < 0.17 ? 1 : 0;             // every 3 s it leans back on the slice, which is too big for it
  ctx.drawImage(SLICE_C, RAT.x, RAT.y + 1);
  ctx.drawImage(RAT_C, RAT.x + 4 + tug, RAT.y);
}
function drawBuilding(t) {
  ctx.drawImage(BLD_BACK, 0, 0);
  for (const w of BWIN) {
    if (w.kind === 'tree') drawXmasTree(w, t);
    else if (w.kind === 'tv') drawTV(w, t);
    else if (w.kind === 'blinds') drawBlinds(w);
    else if (w.kind === 'plant') drawPlant(w);
    else if (w.kind === 'warm') drawWarm(w, 1, t);
    else if (w.kind === 'dim') drawWarm(w, 0.55);
    else if (hash(w.id + 50, Math.floor(frac(t / LOOP + w.off) * w.n), 4) < w.p) drawWarm(w, 1);
  }
  ctx.drawImage(SNOWMAN_C, SNOWMAN.x, SNOWMAN.y);
  drawRat(t);
  // bodega: neon OPEN sign with the odd buzz, cat's tail, warm spill on the sidewalk
  let m = 0.92 + 0.08 * wave(t, 2400);
  if (inEvent(t, NEON_BUZZ)) { const hv = hash(9, Math.floor(t * 30)); m = hv > 0.5 ? 1 : hv > 0.25 ? 0.3 : 0; }
  rect(ctx, 380, 222, 23, 11, '#1a0c10');
  ctx.fillStyle = m > 0.5 ? '#6aa0ff' : m > 0 ? '#3a5aa0' : '#2a2a3a';
  ctx.fillRect(381, 223, 21, 1); ctx.fillRect(381, 231, 21, 1); ctx.fillRect(381, 223, 1, 9); ctx.fillRect(401, 223, 1, 9);
  text(ctx, 'OPEN', 384, 225, m > 0.5 ? '#ff5a6a' : m > 0 ? '#a83a4a' : '#4a2a2a');
  halo(HL.neon, 391, 227, m);
  const tail = wave(t, 40, 0.3) + (hash(3, step(t, 120)) < 0.15 ? 1 : 0);
  px(ctx, 426, 250 - (tail > 0.6 ? 1 : 0), '#1a1210'); px(ctx, 427, 249 - (tail > 0.6 ? 1 : 0), '#1a1210');
  halo(HL.shop, 405, 236, 1); halo(HL.shop, 468, 236, 0.6);
  halo(HL.sign, 410, 214, 0.5);
  ctx.drawImage(BLD_FRONT, 0, 0);
  // a string of holiday bulbs sagging along the second landing's rail
  const ry = FLOORY[1] + WIN_H + 3 - 8, ls = step(t, 480);
  for (let x = FE.x0 + 1, i = 0; x < FE.x1; x += 3, i++) {
    const sag = Math.round(2 * Math.sin(Math.PI * ((x - FE.x0) % 12) / 12));
    px(ctx, x, ry + 1 + sag, hash(i, ls >> 1, 61) > 0.2 ? XMAS[(i + (ls >> 4)) % 5] : '#3a2226');
  }
  // pigeons huddled on the snowy cornice, bobbing now and then
  PIGEONS.forEach((gx, i) => {
    const bob = hash(i, step(t, 160), 44) < 0.3 ? 1 : 0, gy = ROOF - 5;
    rect(ctx, gx + 1, gy, 3, 2, '#5e6678'); px(ctx, gx + 2, gy, '#8a92a6'); px(ctx, gx + 4, gy + 1, '#3c404e');
    px(ctx, gx, gy - 1 + bob, '#7c8498'); px(ctx, gx, gy + bob, '#5a8a80'); px(ctx, gx - 1, gy + bob, '#2e2e38');
  });
}

/* ================= Wind: one loop-safe gust ================= */
// Displacement field G(t) = D * (smoothstep over the gust window - t / LOOP). G(0) = G(240) = 0, so it wraps
// seamlessly; anything airborne is pushed by G(now) - G(birth). The -D/240 px/s baseline is imperceptible.
const GUST = { a: 108, b: 117, D: -170 };
function gustX(t) {
  const tt = ((t % LOOP) + LOOP) % LOOP;
  return GUST.D * (smooth(GUST.a, GUST.b, tt) - tt / LOOP);
}
const gustAmt = t => env(t, GUST.a - 1, GUST.b + 1, 3);

/* ================= Snow at three depths ================= */
const SNOW = [
  { count: 280, nMin: 18, nMax: 26, size: 1, col: 'rgba(168,186,222,0.62)', amp: 2.5, drift: -10, y1: [96, SHORE], gk: 0.35, lights: false },
  { count: 190, nMin: 28, nMax: 36, size: 1, col: 'rgba(222,232,250,0.92)', amp: 4.5, drift: -20, y1: [ROAD0 - 4, WALK + 8], gk: 0.65, lights: true },
  { count: 76, nMin: 40, nMax: 50, size: 2, col: 'rgba(242,246,255,0.95)', amp: 8, drift: -34, y1: [H + 4, H + 4], gk: 1, lights: true },
];
SNOW.forEach((L, li) => {
  const r = rng(300 + li);
  L.flakes = [];
  for (let i = 0; i < L.count; i++) L.flakes.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r(), k: 20 + Math.floor(r() * 50), p: r() });
});
// warm / green light volumes that tint the flakes passing through them: [cx, cy, rx, ry, colour]
const SNOW_LIGHTS = [
  [LAMP.x, 200, 44, 62, 'rgba(255,214,150,0.98)'], [LAMP.x, 152, 24, 18, 'rgba(255,230,180,1)'],
  [405, 234, 36, 24, 'rgba(255,212,150,0.95)'], [CART.x + 17, 248, 20, 12, 'rgba(255,214,160,0.95)'],
  [SUB.x0 + 1, 236, 12, 10, 'rgba(150,255,190,0.95)'], [SUB.x1 + 1, 236, 12, 10, 'rgba(150,255,190,0.95)'],
];
const SNOW_LIGHTS_BB = [...SNOW_LIGHTS, [BB.x + (BB.w >> 1), BB.y - 3, 32, 6, 'rgba(255,230,176,0.98)']];   // + the billboard's floodlamps
// a picked weather (site/js/engine/weather.js): New York draws its own snow; the snow lying on the ledges, the bridge and
// the street stays in every pick, and so does the spindrift the gust whips off it. Rain and leaves land on the
// Manhattan shore, the street and past the bottom of the frame, and catch the same lights as the snow
const WEATHER = weatherPlans(['snow']);
const WX_LAND = [[150, SHORE], [ROAD0 - 4, WALK + 8], [H + 4, H + 4]];
const wxHex = c => '#' + c.match(/\d+/g).slice(0, 3).map(v => (+v).toString(16).padStart(2, '0')).join('');   // rgba(r,g,b,a) -> #rrggbb
const wxLights = list => list.map(([x, y, rx, ry, c]) => ({ x, y, rx, ry, col: wxHex(c) }));
const WX = { land: WX_LAND, lights: wxLights(SNOW_LIGHTS) };
const WX_BB = { land: WX_LAND, lights: wxLights(SNOW_LIGHTS_BB) };
function drawSnow(L, t, beams, lights) {
  const span = W + 40, g = gustX(t);
  for (const f of L.flakes) {
    const q = t * f.n / LOOP + f.ph, fq = Math.floor(q), cyc = ((fq % f.n) + f.n) % f.n, u = q - fq;
    const yEnd = lerp(L.y1[0], L.y1[1], hash(f.id, cyc, 2)), y = -4 + u * (yEnd + 4);
    let x = hash(f.id, cyc, 1) * span + u * L.drift + L.amp * Math.sin(TAU * (f.k * t / LOOP + f.p)) + (g - gustX(t - u * LOOP / f.n)) * L.gk;
    x = ((x % span) + span) % span - 20;
    let col = L.col;
    if (L.lights) {
      for (const [cx, cy, rx, ry, lc] of lights) {
        const dx = (x - cx) / rx, dy = (y - cy) / ry;
        if (dx * dx + dy * dy < 1) { col = lc; break; }
      }
      for (const b of beams) {
        const d = (x - b.hx) * b.dir;
        if (d > 0 && d < 70 && y > b.hy - 2 - d * 0.05 && y < b.hy + 2 + d * 0.16) { col = '#ffffff'; break; }
      }
    }
    ctx.fillStyle = col;
    ctx.fillRect(Math.round(x), Math.round(y), L.size, L.size);
  }
}
// snow dust whipped off the cornice and the water tower cone during the gust
function drawSpindrift(t) {
  const gA = gustAmt(t);
  if (gA < 0.02) return;
  ctx.fillStyle = '#eef4ff';
  for (let i = 0; i < 80; i++) {
    const q = t * 160 / LOOP + i / 80, fq = Math.floor(q), u = q - fq;
    const roof = hash(i, fq, 71) < 0.6;
    const sx = roof ? BX - 3 + hash(i, fq, 72) * 60 : TANK.cx - 12 + hash(i, fq, 72) * 24;
    const sy = roof ? ROOF - 3 : TANK.top - 8 + hash(i, fq, 73) * 6;
    const x = sx - u * 80 * (0.5 + hash(i, fq, 74)), y = sy + u * 22 * hash(i, fq, 75) - Math.sin(u * 3) * 4;
    ctx.globalAlpha = gA * (1 - u) * 0.9;
    ctx.fillRect(Math.round(x), Math.round(y), u < 0.6 ? 2 : 1, 1);
  }
  ctx.globalAlpha = 1;
}

/* ================= Steam and smoke ================= */
const STEAM = [];
{ const r = rng(61); for (let i = 0; i < 38; i++) STEAM.push({ n: 44 + Math.floor(r() * 12), ph: r(), k: 20 + Math.floor(r() * 20), p: r(), s: 0.6 + r() * 0.8 }); }
const CUP = [];
{ const r = rng(62); for (let i = 0; i < 9; i++) CUP.push({ n: 60 + Math.floor(r() * 12), ph: r(), k: 30 + Math.floor(r() * 20), p: r() }); }
const SMOKE = [];
{ const r = rng(63); for (let i = 0; i < 16; i++) SMOKE.push({ n: 30 + Math.floor(r() * 8), ph: r(), k: 20 + Math.floor(r() * 20), p: r() }); }
function puffs(list, t, x0, y0, rise, drift, rMax, alpha, gk, warmUntil) {
  const g = gustX(t);
  for (const s of list) {
    const q = t * s.n / LOOP + s.ph, u = q - Math.floor(q), born = t - u * LOOP / s.n;
    const x = x0 + (0.35 * u + 0.65 * u * u) * drift * (s.s || 1) + wave(t, s.k, s.p) * 2.5 * u + (g - gustX(born)) * gk;
    const y = y0 - u * rise + u * u * rise * 0.25;
    const rr = Math.max(1, Math.min(rMax, 1 + Math.floor(u * (rMax + 1))));
    ctx.globalAlpha = alpha * Math.min(1, u * 16) * Math.pow(1 - u, 1.2);
    ctx.drawImage(u < warmUntil ? PUFF_W[rr] : PUFF_C[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
}

/* ================= A plane crossing high up ================= */
const PLANE = { t0: 24, t1: 104 };
function drawPlane(t) {
  if (t < PLANE.t0 || t >= PLANE.t1) return;
  const u = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(lerp(-10, 392, u)), y = Math.round(31 - u * 12);
  rect(ctx, x - 1, y, 4, 1, '#1a2238');
  px(ctx, x - 1, y, frac(t / 1.5) < 0.5 ? '#ff4a4a' : '#6a2020');
  px(ctx, x + 2, y, '#5aff8a');
  if (frac(t) < 0.12) { px(ctx, x, y - 1, '#ffffff'); halo(HL.white, x, y - 1, 0.9); }
}

/* ================= Yellow cabs ================= */
const CAB_ROWS = [
  '.'.repeat(11) + 'nnnn' + '.'.repeat(16),
  '.'.repeat(11) + 'lLLl' + '.'.repeat(16),
  '.'.repeat(8) + 'n'.repeat(11) + '.'.repeat(12),
  '.'.repeat(7) + 'o' + 'y'.repeat(11) + 'o' + '.'.repeat(11),
  '.'.repeat(6) + 'oyGgggyyyGgggyo' + '.'.repeat(10),
  '.'.repeat(5) + 'oygggggyyygggggyo' + '.'.repeat(9),
  '..ooo' + 'y'.repeat(22) + 'o...',
  '.oyy' + 'Y'.repeat(25) + 'o.',
  'royy' + 'kw'.repeat(11) + 'kyyyh',
  'ro' + 'y'.repeat(28) + 'c',
  '.ooo' + 'ksssk' + 'o'.repeat(14) + 'ksssk' + 'oo.',
  '....smmms' + '.'.repeat(14) + 'smmms...',
  '.....sss' + '.'.repeat(16) + 'sss....',
];
const CAB_KEY = { y: '#f2c12e', Y: '#ffdc62', o: '#a07416', k: '#121216', w: '#f2f2ee', g: '#18243c', G: '#6a8cc0',
  l: '#fff4c0', L: '#e8c050', s: '#0e0e12', m: '#8a8e9a', r: '#ff3a2a', h: '#fffbe6', c: '#c8ccd6', n: '#eef4ff' };
// four passes per loop, alternating direction (eastbound in the near lane, westbound in the far lane)
const CABS = [{ t0: 16, dur: 9, dir: 1 }, { t0: 70, dur: 9.5, dir: -1 }, { t0: 131, dur: 8.5, dir: 1 }, { t0: 192, dur: 9, dir: -1 }];
const BEAM_W = 72, BEAM_H = 18, BEAM_OY = 5;
function renderBeam(flip) {
  const p = painter(BEAM_W, BEAM_H);
  for (let j = 0; j < BEAM_H; j++) for (let i = 0; i < BEAM_W; i++) {
    const dy = j - BEAM_OY, up = -1 - i * 0.05, dn = 1 + i * 0.16;
    let a = 0;
    if (dy >= up && dy <= dn) a = 0.2 * Math.pow(1 - i / BEAM_W, 1.3) * (1 - 0.5 * Math.abs(dy) / Math.max(1, dn));
    const pd = Math.hypot((i - 40) / 28, (j - BEAM_OY - 5) / 3.2);        // pool of light on the slush ahead
    if (pd < 1) a += 0.22 * (1 - pd);
    const q = Math.floor(a * 30 + bayer(i, j)) / 30;
    if (q > 0) p.put(flip ? BEAM_W - 1 - i : i, j, '#fff0c8', Math.round(Math.min(1, q) * 255));
  }
  return p.done();
}
function activeCabs(t) {
  const out = [];
  for (const c of CABS) {
    if (t < c.t0 || t >= c.t0 + c.dur) continue;
    const u = (t - c.t0) / c.dur, x = Math.round(c.dir > 0 ? lerp(-40, BX + 4, u) : lerp(BX + 4, -40, u)), y = c.dir > 0 ? 239 : 228;
    out.push({ x, y, dir: c.dir, hx: c.dir > 0 ? x + 31 : x - 1, hy: y + 8 });
  }
  return out;
}
function drawCabs(t, cabs) {
  for (const c of cabs) {
    const spr = c.dir > 0 ? CAB_R : CAB_L, tx = c.dir > 0 ? c.x - 1 : c.x + 31;
    for (let j = 0; j < 5; j++) {                              // mirrored in the wet slush
      ctx.globalAlpha = 0.3 * (1 - j / 5);
      ctx.drawImage(spr, 0, 10 - j, 31, 1, c.x, c.y + 13 + j, 31, 1);
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'lighter';
    ctx.drawImage(c.dir > 0 ? BEAM_R : BEAM_L, c.dir > 0 ? c.hx : c.hx - BEAM_W + 1, c.hy - BEAM_OY);
    ctx.globalCompositeOperation = 'source-over';
    ctx.drawImage(spr, c.x, c.y);
    halo(HL.head, c.hx, c.hy, 1); halo(HL.tail, tx, c.hy, 0.9);
    streak(ctx, t, c.hx + c.dir * 8, 6, '#fff0c0', 0.35, 30, c.y + 13, c.y + 21);
    streak(ctx, t, tx, 2, '#ff4030', 0.45, 31, c.y + 13, c.y + 19);
    const s = step(t, 2400);                                   // slush thrown up by the rear wheel
    ctx.fillStyle = '#b8c4da';
    for (let k = 0; k < 7; k++) {
      if (hash(k, s, 55) < 0.4) continue;
      ctx.globalAlpha = 0.75 * (1 - k / 7);
      ctx.fillRect(Math.round((c.dir > 0 ? c.x + 4 : c.x + 26) - c.dir * (1 + k * 1.6)), Math.round(c.y + 12 - hash(k, s, 56) * 3 - k * 0.3), 1, 1);
    }
    ctx.globalAlpha = 1;
  }
}

/* ================= Street lamp ================= */
const LAMP_FLICK = [[61, 62.8], [177.5, 179.6]];
function lampI(t) {
  let v = 0.95 + 0.05 * wave(t, 1500, 0.3);
  if (inEvent(t, LAMP_FLICK)) { const hv = hash(11, Math.floor(t * 20)); v *= hv > 0.45 ? 1 : hv > 0.2 ? 0.45 : 0.15; }
  return v;
}
function renderCone() {
  const w = 96, h = H - LAMP.y - 4, p = painter(w, h), cx = 48;
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const hw = 3 + j * 0.38, d = Math.abs(i - cx) / hw;
    if (d >= 1) continue;
    const a = 0.17 * Math.pow(1 - j / h, 0.6) * (1 - d * d);
    const q = Math.floor(a * 40 + bayer(i, j)) / 40;
    if (q > 0) p.put(i, j, '#ffc47a', Math.round(q * 255));
  }
  return p.done();
}
function renderVignette() {
  const [c, x] = makeCanvas(W, H), img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.75, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 2; d[o + 1] = 6; d[o + 2] = 16; d[o + 3] = Math.round(q * 0.45 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Shore traffic ================= */
const FDR = [];
{ const r = rng(19); for (let i = 0; i < 16; i++) FDR.push({ dir: i % 2 ? -1 : 1, n: 6 + Math.floor(r() * 6), ph: r() }); }

/* ================= Pedestrians on the waterfront sidewalk ================= */
const PERSON = ['..w..', '.hhh.', '.aff.', '.rrr.', '.cccc', '.cCcc', '.cCcc', '.cccc', '..cc.'];
const LEGS = [['.l..l', 'l...l'], ['..ll.', '..ll.']];
const DOG = [['......e', '.....dd', 'tSSSSdd', '.d...d.'], ['......e', '.....dd', 'tSSSSdd', '..d.d..']];
const UMBRELLA = ['...nnn...', '.nuuuuun.', 'uuuuuuuuu', '....k....', '....k....'];
const PED_KEY = { h: '#c83a44', w: '#f2f2f6', a: '#3a2a22', f: '#e2aa82', r: '#efe4cc', c: '#262b3c', C: '#3c4560', l: '#12151e',
  e: '#3a2618', d: '#5a3a26', t: '#5a3a26', S: '#c8303a', u: '#2a3350', n: '#eef4ff', k: '#1a1a22' };
const WALKERS = [{ t0: 32, t1: 80, dir: 1, dog: true }, { t0: 140, t1: 188, dir: -1, umbrella: true }];
function drawRows(rows, x, y, flip, key = PED_KEY) {
  const w = rows[0].length;
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) px(ctx, x + (flip ? w - 1 - i : i), y + j, col); } });
}
function drawWalkers(t) {
  for (const w of WALKERS) {
    if (t < w.t0 || t >= w.t1) continue;
    const u = (t - w.t0) / (w.t1 - w.t0), x = Math.round(w.dir > 0 ? lerp(-14, BX + 8, u) : lerp(BX + 8, -14, u));
    const fy = ROAD0 - 3, fr = step(t, 960) & 1, flip = w.dir < 0;
    if (w.dog) {
      const dx = x + w.dir * 11;
      ctx.fillStyle = '#7a3a3a';
      line(ctx, x + (flip ? 0 : 4), fy - 6, dx + (flip ? 1 : 5), fy - 2, '#7a3a3a');   // leash
      drawRows(DOG[fr], dx, fy - 3, flip);
    }
    drawRows(PERSON, x, fy - 10, flip);
    drawRows(LEGS[fr], x, fy - 1, flip);
    if (w.umbrella) drawRows(UMBRELLA, x - 2, fy - 15, flip);
  }
}

/* ================= Billboard on the promenade ================= */
// Steel columns with X-bracing on the Brooklyn promenade, a snowy catwalk with a ladder, a framed face and three
// small gooseneck lamps curving over its snow-capped top edge. The city's own ad is a hot dog stand's poster, lit like
// the night around it (BB_NIGHT); render(t, { pitch: true }) paints the pitch on the face instead (everything around
// it stays identical).
const BB_X0 = BB.x - 2, BB_X1 = BB.x + BB.w + 1, BB_Y0 = BB.y - 2, BB_Y1 = BB.y + BB.h + 1;   // outer frame
const BB_LAMPS = [0.18, 0.5, 0.82].map(f => BB.x + Math.round((BB.w - 1) * f));   // gooseneck lamp heads
const BB_LENS = BB_Y0 - 4;                                   // lens row, above the top edge
const BB_COLS = [BB.x + 7, BB.x + BB.w - 9];                 // the two lattice columns (2 px wide)
const BB_FEET = 222;                                         // base plates on the promenade
const BBC = { x: BB_X0 - 6, y: BB_LENS - 4, w: BB.w + 16, h: BB_FEET + 2 - (BB_LENS - 4) };   // structure sprite
const BB_FLICK = [[47, 48.4], [131.5, 132.8], [203, 204.1]];  // the middle lamp buzzes now and then
function renderBillboard() {
  const p = painter(BBC.w, BBC.h), put = (i, y, col) => p.put(i - BBC.x, y - BBC.y, col);
  const pline = (x0, y0, x1, y1, col) => {                    // Bresenham into the painter
    const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
    let err = dx + dy;
    for (;;) {
      put(x0, y0, col);
      if (x0 === x1 && y0 === y1) break;
      const e2 = 2 * err;
      if (e2 >= dy) { err += dy; x0 += sx; }
      if (e2 <= dx) { err += dx; y0 += sy; }
    }
  };
  const snow = (i, y, seed, dens) => { const h1 = hash(i, seed, 95); if (h1 < dens) put(i, y, snowPx(h1 / dens * 0.9, hash(i, seed + 1, 95))); };
  const X0 = BB_X0, X1 = BB_X1, Y0 = BB_Y0, Y1 = BB_Y1;
  const DK = '#0c0f18', MD = '#1c2232', LT = '#3a4660', HI = '#5a6682';
  // two lattice columns on base plates, snowy girts and X-bracing bay by bay, a knee brace out to the catwalk end
  const top = Y1 + 4, bot = BB_FEET - 1, [c0, c1] = BB_COLS, n = Math.max(1, Math.round((bot - top) / 13));
  for (const cx of BB_COLS) {
    for (let y = Y1 + 3; y < BB_FEET; y++) { put(cx, y, LT); put(cx + 1, y, DK); }
    for (let i = -1; i <= 2; i++) put(cx + i, BB_FEET, MD);
    put(cx - 1, BB_FEET - 1, snowPx(0.2, 0)); put(cx + 2, BB_FEET - 1, snowPx(0.8, 0.8));
  }
  for (let k = 0; k < n; k++) {
    const ya = top + Math.round(k * (bot - top) / n), yb = top + Math.round((k + 1) * (bot - top) / n);
    pline(c0 + 2, ya, c1 - 1, yb, '#2e3850'); pline(c0 + 2, yb, c1 - 1, ya, LT);
    if (k === n - 1) continue;
    for (let i = c0 + 2; i < c1; i++) {
      put(i, yb, MD);
      const h1 = hash(i, k, 96); if (h1 < 0.5) put(i, yb - 1, snowPx(h1 * 1.6, hash(i, k, 97)));
    }
  }
  pline(c1 + 2, top + 6, X1 + 3, Y1 + 3, LT);
  // ladder from the promenade up to the catwalk, beside the left column
  for (let y = Y1 + 3; y < BB_FEET + 1; y++) {
    put(c0 - 4, y, LT); put(c0 - 2, y, DK);
    if ((y & 1) === 0) put(c0 - 3, y, MD);
  }
  // catwalk: grated deck under snow, a steel edge, a beam, icicles, and short railings at both ends
  for (let i = X0 - 4; i <= X1 + 4; i++) {
    snow(i, Y1 + 1, 50, 0.86);
    if (hash(i, 50, 95) >= 0.86) put(i, Y1 + 1, LT);
    put(i, Y1 + 2, i % 3 === 0 ? DK : (i & 1) ? MD : '#262c3e');
    put(i, Y1 + 3, DK);
    if (hash(i, 52, 95) < 0.1) { put(i, Y1 + 4, '#cfe0f4'); if (hash(i, 53, 95) < 0.5) put(i, Y1 + 5, '#8ea4c8'); }
  }
  for (const [r0, r1] of [[X0 - 4, X0 - 1], [X1 + 1, X1 + 4]]) {
    const post = r0 < X0 ? r0 : r1;
    for (let y = Y1 - 4; y <= Y1; y++) put(post, y, HI);
    for (let i = r0; i <= r1; i++) { put(i, Y1 - 4, LT); put(i, Y1 - 2, MD); if ((i + post) & 1) snow(i, Y1 - 5, 60, 0.7); }
  }
  // frame: dark outer moulding, inner lip lit at the bottom where the floodlight falls, snow along the top
  for (let i = X0; i <= X1; i++) { put(i, Y0, DK); put(i, Y1, DK); }
  for (let y = Y0; y <= Y1; y++) { put(X0, y, DK); put(X1, y, DK); }
  for (let i = X0 + 1; i < X1; i++) { put(i, Y0 + 1, '#2e3446'); put(i, Y1 - 1, '#5e4c3e'); }
  for (let y = Y0 + 1; y < Y1; y++) { put(X0 + 1, y, '#2a3042'); put(X1 - 1, y, '#161a26'); }
  put(X0 + 1, Y1 - 1, '#3a3438'); put(X1 - 1, Y1 - 1, '#3a3438');
  for (let i = X0 - 1; i <= X1 + 1; i++) {
    snow(i, Y0 - 1, 70, 0.93);
    if (hash(i >> 1, 72, 95) < 0.33) snow(i, Y0 - 2, 74, 0.8);
  }
  // gooseneck lamps: a stalk from the top edge, bending forward into a shade whose open end faces the poster
  // (the lens row is drawn per frame)
  for (const lx of BB_LAMPS) {
    for (let y = BB_LENS + 1; y < Y0 - 1; y++) put(lx, y, y === BB_LENS + 1 ? MD : LT);
    for (let i = lx - 2; i <= lx + 2; i++) put(i, BB_LENS - 1, i === lx - 2 ? LT : i === lx + 2 ? DK : MD);
    for (let i = lx - 1; i <= lx + 1; i++) put(i, BB_LENS - 2, i === lx - 1 ? HI : MD);
    put(lx, BB_LENS - 3, snowPx(0.3, 0)); put(lx - 1, BB_LENS - 3, snowPx(0.8, 0.2));
  }
  return p.done();
}
// the city's own ad: a hot dog in a bun with a mustard zigzag on lamplit tan poster paper, HOT DOGS on a red band
const DOG_W = 30, DOG_H = 7;
function dogKey(u, v) {
  if (v >= 3 && v <= 6) {                                      // front half of the bun
    if (v === 6) return u >= 5 && u <= 24 ? 'D' : null;
    if (u >= 3 && u <= 26) return v === 3 ? 'L' : v === 5 && (u < 5 || u > 24) ? 'D' : 'B';
  }
  if (v >= 1 && v <= 3 && u >= 1 && u <= 28) {                 // the frank, longer than the bun
    if ((v === 1 || v === 3) && (u === 1 || u === 28)) return null;
    if (v <= 2 && u >= 5 && u <= 24 && v === 1 + ((u >> 1) & 1)) return 'm';
    return v === 1 ? 'F' : v === 2 ? 'f' : 'r';
  }
  if (v === 0 && u >= 5 && u <= 24) return 'b';                // back half of the bun, behind the frank
  return null;
}
const DOG_COL = { o: '#5a2414', b: '#c8864a', F: '#f08a5e', f: '#c8482e', r: '#8e2c20', m: '#ffd23f',
  L: '#ffe2a8', B: '#eeae62', D: '#c07a3c' };
const AD_DOG = { x: (BB.w - DOG_W - 2) >> 1, y: 3 };                                 // top-left of the outlined dog on the face
const AD_BAND = 12;                                            // first row of the red band (a string of bulbs)
// the poster at night (gradeAd): pasted paper a winter old under three small lamps, cooled towards the sky behind it,
// about as light as the bridge's stone and well under the Empire State's crown. The bulbs, the glint and the lamps'
// lenses and halos (drawn per frame) are stepped down by hand to match
const BB_NIGHT = { level: 0.78, tint: '#162c4d', mix: 0.1, wear: 0.3, seed: 73 };
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h);
  const PAPER = ['#fbe4b2', '#f2d29a', '#e6bf84', '#d4a970'], BAND = ['#e0443c', '#c42a2e', '#a82228', '#861a22'];
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const lamp = Math.min(...BB_LAMPS.map(lx => Math.abs(BB.x + i - lx))) / 8;   // brighter under each lamp
    const band = y >= AD_BAND, pal = band ? BAND : PAPER;
    const v = clamp(band ? (y - AD_BAND) / (h - 1 - AD_BAND) : y / (AD_BAND - 1) * 0.8 + lamp * 0.12 - (y < 2 ? 0.15 : 0), 0, 1) * (pal.length - 1);
    let k = Math.floor(v); if (v - k > bayer(i, y)) k++;
    p.put(i, y, pal[Math.min(k, pal.length - 1)]);
  }
  for (let v = -1; v <= DOG_H; v++) for (let u = -1; u <= DOG_W; u++) {
    let k = dogKey(u, v);
    if (!k && [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([a, b]) => dogKey(u + a, v + b))) k = 'o';
    if (k) p.put(AD_DOG.x + 1 + u, AD_DOG.y + 1 + v, DOG_COL[k]);
  }
  const c = p.done();
  text(p.x, 'HOT DOGS', (BB.w - 31) >> 1, AD_BAND + 2, '#fff0cc');
  return gradeAd(c, BB_NIGHT);
}
// a chasing string of bulbs along the top of the band and a glint running along the mustard
function drawBillboardAd(t) {
  ctx.drawImage(BB_AD, BB.x, BB.y);
  const s = step(t, 960);                                      // the chase moves 4 times per second
  for (let i = 1, k = 0; i < BB.w - 1; i += 2, k++) {
    const on = (k + s) % 3 !== 0;
    ctx.fillStyle = on ? (k % 3 === 1 ? '#e6c880' : '#efe0b8') : '#4e1519';   // small bulbs, stepped down with the poster
    ctx.fillRect(BB.x + i, BB.y + AD_BAND, 1, 1);
  }
  const g = cycle(t, 60, 0.2);                                  // glint every 4 s
  if (g.u < 0.3) {
    const u = 5 + Math.floor(g.u / 0.3 * 20);
    px(ctx, BB.x + AD_DOG.x + 1 + u, BB.y + AD_DOG.y + 2 + ((u >> 1) & 1), '#eee2ae');
  }
}
// the pool one lamp throws on the top of the face, as a dithered shadow for when that lamp buzzes
function renderBillboardDim() {
  const w = 19, h = 11, p = painter(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++)
    if ((1 - Math.hypot((i - 9) / 9.5, j / h)) * 1.6 > bayer(i, j)) p.put(i, j, '#160c06');
  return p.done();
}
function bbLampI(t, i) {
  let v = 0.96 + 0.04 * wave(t, 1800, i * 0.3);
  if (i === 1 && inEvent(t, BB_FLICK)) { const hv = hash(17, Math.floor(t * 24)); v *= hv > 0.5 ? 1 : hv > 0.22 ? 0.4 : 0.08; }
  return v;
}
function drawBillboard(t, pitch) {
  halo(HL.bbWash, BB.x + (BB.w >> 1), BB.y + 4, 0.9);                              // light spilling onto the river and the railing
  ctx.drawImage(BB_C, BBC.x, BBC.y);
  if (!PITCH.covers(t, pitch)) drawBillboardAd(t);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, i) => {
    const li = bbLampI(t, i);
    if (li < 0.9) {                                            // a buzzing lamp leaves its pool on the face dimmer
      ctx.globalAlpha = (0.9 - li) * 0.55;
      ctx.drawImage(BB_DIM, lx - (BB_DIM.width >> 1), BB.y);
      ctx.globalAlpha = 1;
    }
    ctx.fillStyle = li > 0.6 ? '#f2dcaa' : li > 0.2 ? '#a88a62' : '#4a3a30';
    ctx.fillRect(lx - 1, BB_LENS, 3, 1);
    halo(HL.bbLamp, lx, BB_LENS + 1, li);
  });
}

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, CLOUDS, SKYLINE, ESB_C, CHR, TOWER_C, BRIDGE_C, WATER_C, FERRY_C, TUG_C, STREET_C, FRONT_C,
  BLD_BACK, BLD_FRONT, VIG_C, CAB_R, CAB_L, BEAM_R, BEAM_L, CONE_C, PUFF_W, PUFF_C, BB_C, BB_AD, BB_DIM, PITCH, SNOWMAN_C, RAT_C, SLICE_C;
function prerender() {
  HL = {
    moon: makeHalo(44, '#8fb0ff', 0.24, 1.8), moonCore: makeHalo(16, '#e0eaff', 0.35, 1.5), city: makeHalo(170, '#ff9a5a', 0.07, 1.3),
    esbW: makeHalo(40, '#ffe8c0', 0.22, 1.6), esbR: makeHalo(36, '#ff4a3a', 0.24, 1.6), esbG: makeHalo(26, '#3aff6a', 0.2, 1.6), esbB: makeHalo(40, '#4a86ff', 0.26, 1.6),
    chr: makeHalo(26, '#dfe8ff', 0.18, 1.6), owt: makeHalo(18, '#cfe0ff', 0.12, 1.6), owtBeacon: makeHalo(6, '#eef4ff', 0.5, 1.3), red: makeHalo(4, '#ff3030', 0.6, 1), white: makeHalo(5, '#ffffff', 0.5, 1), green: makeHalo(4, '#5aff8a', 0.5, 1),
    tower: makeHalo(46, '#ffb870', 0.13, 1.6), towerTop: makeHalo(26, '#ffc890', 0.1, 1.6), deckLamp: makeHalo(6, '#ffd08a', 0.35),
    sirenR: makeHalo(12, '#ff3030', 0.55, 1.4), sirenB: makeHalo(12, '#3a6aff', 0.6, 1.4), sirenR2: makeHalo(34, '#ff3030', 0.16, 1.5), sirenB2: makeHalo(34, '#3a6aff', 0.18, 1.5),
    ferry: makeHalo(18, '#ffc27a', 0.22), win: makeHalo(11, '#ffb45a', 0.14), tv: makeHalo(13, '#5a8cff', 0.3), tree: makeHalo(12, '#ff8adf', 0.12),
    neon: makeHalo(18, '#ff4a6a', 0.35), shop: makeHalo(40, '#ffae5a', 0.18), sign: makeHalo(30, '#6ad0a0', 0.1),
    lamp: makeHalo(60, '#ffb060', 0.26, 1.7), lampCore: makeHalo(16, '#ffe2a8', 0.5, 1.4),
    globe: makeHalo(16, '#40ff80', 0.35, 1.5), subway: makeHalo(20, '#ffb870', 0.16), cart: makeHalo(22, '#ffb860', 0.25), head: makeHalo(8, '#fff4d0', 0.6, 1.3), tail: makeHalo(5, '#ff3020', 0.55, 1.2),
    bbLamp: makeHalo(6, '#ffc878', 0.3, 1.6), bbWash: makeHalo(40, '#ffb870', 0.06, 1.5),   // small lamps: a glow on the lens, a faint wash behind
  };
  SKY_C = renderSky(); MOON_C = renderMoon();
  CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  SKYLINE = renderSkyline();
  ESB_C = ESB_STATES.map(renderESBCrown);
  CHR = renderChryslerCrown();
  TOWER_C = renderBridgeTower(); BRIDGE_C = renderBridgeBack();
  WATER_C = renderWater(); FERRY_C = sprite(FERRY_ROWS, FERRY_KEY); TUG_C = sprite(TUG_ROWS, TUG_KEY);
  for (const f of FLOES) f.c = renderFloe(f.w, f.big);
  STREET_C = renderStreet(); FRONT_C = renderStreetFront();
  BLD_BACK = renderBldBack(); BLD_FRONT = renderBldFront();
  SNOWMAN_C = sprite(SNOWMAN_ROWS, SNOWMAN_KEY); RAT_C = sprite(RAT_ROWS, RAT_KEY); SLICE_C = sprite(SLICE_ROWS, RAT_KEY);
  VIG_C = renderVignette();
  CAB_R = sprite(CAB_ROWS, CAB_KEY); CAB_L = sprite(CAB_ROWS, CAB_KEY, true);
  BEAM_R = renderBeam(false); BEAM_L = renderBeam(true); CONE_C = renderCone();
  BB_C = renderBillboard(); BB_AD = renderBillboardAd(); BB_DIM = renderBillboardDim();
  PITCH = makePitch(BB.w, BB.h, { accent: '#ffd23f' });
  PUFF_W = [null]; PUFF_C = [null];
  for (let r = 1; r <= 12; r++) { PUFF_W.push(makePuff(r, '#f4eee8', '#c8c0c0')); PUFF_C.push(makePuff(r, '#e2e8f4', '#aab6ca')); }
}

/* ================= Frame compositor (back to front) ================= */
function drawScroll(cv, y, k, t) {
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
const ESB_HALOS = [[['esbW', 70, 1]], [['esbR', 80, 0.95], ['esbG', 68, 0.85], ['esbR', 52, 0.6]], [['esbB', 70, 1]]];
// billboard: false leaves it out (the loop videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's own
// when left out
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const mix = esbMix(t), cabs = activeCabs(t), li = lampI(t), lights = billboard ? SNOW_LIGHTS_BB : SNOW_LIGHTS;
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  // sky, stars, moon, clouds, plane
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#dfe8ff';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - 7, MOON.y - 7);
  drawSleigh(t);
  for (const L of CLOUDS) drawScroll(L.c, L.y, L.k, t);
  drawPlane(t);
  halo(HL.city, 200, 196, 1);
  // Manhattan skyline: toggling windows, aviation lights, One WTC beacon, Empire State crown, Chrysler crown
  ctx.drawImage(SKYLINE.c, 0, 0);
  for (const w of SKYLINE.wins) if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p) px(ctx, w.x, w.y, w.col);
  for (const a of AVI) { const on = frac(t / a.per + a.ph) < 0.35; px(ctx, a.x, a.y, on ? '#ff3a3a' : '#4a1418'); if (on) halo(HL.red, a.x, a.y, 0.9); }
  // One World Trade Center: parapet glow and the spire beacon's slow white pulse (every 1.5 s)
  halo(HL.owt, OWT.x, OWT.roof, 0.85 + 0.15 * wave(t, 20));
  const ob = 0.5 + 0.5 * wave(t, 160);
  px(ctx, OWT.x, OWT.tip - 1, ob > 0.45 ? '#ffffff' : '#8a96b4'); halo(HL.owtBeacon, OWT.x, OWT.tip - 1, ob);
  const [ea, eb, eu] = mix, pulse = 0.9 + 0.1 * wave(t, 16);
  ctx.drawImage(ESB_C[ea], ESB_CROWN.x0, ESB_CROWN.y0);
  if (eu > 0) { ctx.globalAlpha = eu; ctx.drawImage(ESB_C[eb], ESB_CROWN.x0, ESB_CROWN.y0); ctx.globalAlpha = 1; }
  for (const [k, y, v] of ESB_HALOS[ea]) halo(HL[k], ESB_X, y, v * (1 - eu) * pulse);
  if (eu > 0) for (const [k, y, v] of ESB_HALOS[eb]) halo(HL[k], ESB_X, y, v * eu * pulse);
  const beacon = frac(t / 2) < 0.3;
  px(ctx, ESB_X, 22, beacon ? '#ff4040' : '#5a1a1a'); if (beacon) halo(HL.red, ESB_X, 22, 1);
  ctx.drawImage(CHR.c, CHR.ox, CHR.oy);
  halo(HL.chr, CHR_X, 96, 0.9 + 0.1 * wave(t, 12));
  const cs = step(t, 480);
  ctx.fillStyle = '#ffffff';
  CHR.pts.forEach(([x, y], i) => { if (hash(i, cs, 45) < 0.06) ctx.fillRect(x, y, 1, 1); });
  for (const c of FDR) {
    const u = frac(t * c.n / LOOP + c.ph), x = Math.round((c.dir > 0 ? u : 1 - u) * (W + 10)) - 5;
    px(ctx, x, SHORE - 5 + (c.dir > 0 ? 1 : 0), c.dir > 0 ? '#ffe8b0' : '#ff4a3a');
  }
  if (wx.snow) drawSnow(SNOW[0], t, cabs, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // Brooklyn Bridge
  ctx.drawImage(BRIDGE_C, 0, 0);
  for (const lx of DECK_LAMPS) halo(HL.deckLamp, lx, DECK - 7, 0.85 + 0.15 * wave(t, 600 + lx));
  drawDeckCars(t);
  drawPromenade(t);
  const siren = drawSiren(t);
  TW.forEach((tc, i) => {
    halo(HL.tower, tc, 152, 0.95); halo(HL.towerTop, tc, 104, 0.9);
    ctx.drawImage(TOWER_C, tc - 21, T_TOP - 1);
    drawFlag(tc, t, i);
  });
  // East River
  drawRiver(t, mix, siren);
  drawFerry(t);
  drawTug(t);
  // street: slush reflections, mid snow, cabs, then the kerb-side props
  ctx.drawImage(STREET_C, 0, 0);
  if (billboard) drawBillboard(t, pitch);
  streak(ctx, t, LAMP.x, 5, '#ffc070', 0.32 * li, 40, ROAD0 + 3, WALK - 2);
  drawWalkers(t);
  if (wx.snow) drawSnow(SNOW[1], t, cabs, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawCabs(t, cabs);
  ctx.drawImage(FRONT_C, 0, 0);
  const gl = 0.85 + 0.15 * wave(t, 30);
  halo(HL.globe, SUB.x0 + 1, 236, gl); halo(HL.globe, SUB.x1 + 1, 236, gl); halo(HL.subway, (SUB.x0 + SUB.x1) / 2 + 1, WALK + 4, 0.9);
  halo(HL.cart, CART.x + 17, 249, 0.9);
  puffs(STEAM, t, STACK.x, STACK.top - 2, 74, -30, 11, 0.8, 0.8, 0.3);
  puffs(CUP, t, CART.x + 34, CART.y - 7, 20, -9, 4, 0.6, 0.6, 0.5);
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = li;
  ctx.drawImage(CONE_C, LAMP.x - 48, LAMP.y + 4);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  if (li < 0.9) rect(ctx, LAMP.x - 1, 149, 3, 6, `rgba(30,18,8,${(0.9 - li).toFixed(3)})`);
  halo(HL.lamp, LAMP.x, LAMP.y, li); halo(HL.lampCore, LAMP.x, LAMP.y, li);
  // brick walk-up, chimney smoke
  drawBuilding(t);
  puffs(SMOKE, t, CHIM.x - 0.5, CHIM.y - 1, 40, -30, 6, 0.4, 1, 0);
  // weather in front of everything
  if (wx.snow) drawSnow(SNOW[2], t, cabs, lights);
  drawSpindrift(t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'new-york',
  name: 'New York',
  tagline: 'Snow over Brooklyn Bridge',
  timeZone: 'America/New_York',
  accent: '#ffd23f',
  focusX: ESB_X,
  landmark: [ESB_X - 11, ESB_X + 12],                  // the Empire State Building
  landmarkTop: 22,                                     // the row of the antenna's beacon, which landscape crops keep
  billboard: BB,
  spots: [                                             // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: ESB_X - 11, y: 30, w: 23, h: DECK - 30 },                     // the Empire State Building, above the bridge
    { kind: 'water', x: TW[0] + 20, y: SHORE + 2, w: TW[1] - TW[0] - 40, h: 21 },        // the East River between the bridge's towers
    { kind: 'window', x: COLX[1], y: FLOORY[1], w: WIN_W, h: WIN_H },                    // a walk-up window behind the fire escape
    { kind: 'birds', x: PIGEONS[0] - 3, y: ROOF - 10, w: 21, h: 9 },                     // the pigeons huddled on the cornice
  ],
  secrets: [                                           // hidden things a tap finds, each a passport sticker (site/js/ui/secrets.js)
    { id: 'snowman', name: 'a snowman on the roof', x: SNOWMAN.x - 1, y: SNOWMAN.y - 1, w: 9, h: 12, art: [
      '....KKKK....', '...KKKKKK...', '....wwwW....', '....kwkW....', '....wooW....', '.N..rrrr..N.',
      '..NwwwwwWN..', '...wwkwwW...', '..wwwwwwwW..', '..wwwkwwwW..', '..wwwwwwWW..', '...wwwwWW...'] },
    { id: 'rat', name: 'a rat with a slice of pizza', x: RAT.x - 1, y: RAT.y - 1, w: 14, h: 6, art: [
      '............', '............', '.......pp...', '......pSSSS.', 'nyy...SkSSSS', 'nyryypSSSSSS',
      'nyyyy.SSSSSS', 'nyy....SSSSS', '.......n..nP', '...........P', '..........PP', '............'] },
    { id: 'sleigh', name: 'a sleigh across the moon', x: MOON.x - 8, y: MOON.y - 8, w: 16, h: 16, at: SLEIGH, art: [
      '....yyyy....', '..yyyyyyyy..', '.yyyyyyyyyy.', '.yKyyKyyyyy.', 'yKKyKKyyyKyy', 'yyKKKKyKKKyy',
      'yyKyKyyKKKKy', 'yyyyyyKyyyKy', '.yyyyyyyyyy.', '.yyyyyyyyyy.', '..yyyyyyyy..', '....yyyy....'] },
  ],
  ambience: { wind: 0.45, traffic: 0.45, water: 0.15, crowd: 0.1 },
  events: [{ t: 19, type: 'horn' }, { t: 88, type: 'horn' }, { t: 150.5, type: 'siren' }, { t: 195.5, type: 'horn' }],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
